import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { colors, cycleStatusLabel } from "@/theme/colors";
import { ApiError } from "@/api/client";
import {
  createConnection,
  createManualObject,
  deleteConnection,
  deleteObject,
  getCycle,
  getLock1Summary,
  getLock2Summary,
  patchObject,
  postLock1,
  postLock2,
  preferredAssetUrl,
  resolveManualTrashConflict,
  resolveOverlapPair,
  setOperationalState,
  OPERATIONAL_STATES,
  operationalStateLabel,
  type AnnotationObject,
  type BBox,
  type Connection,
  type CycleAnnotations,
  type CycleDownloads,
  type Lock1Summary,
  type Lock2Summary,
  type ManualTrashConflictWire,
  type ManualTrashDecision,
  type ObjectStatus,
  type OperationalState,
  type OverlapDecision,
  type OverlapPairWire,
} from "@/api/cycles";
import { createSku } from "@/api/skus";
import { PageCanvas, type CanvasOverride } from "@/components/PageCanvas";
import { SkuPicker } from "@/components/SkuPicker";
import { useSkuCatalog, lookupSku } from "@/components/sku";
import { TagList } from "@/components/TagList";

const ACTIVE_REFETCH_MS = 1500;
const IDLE_REFETCH_MS = 5000;

// Post-PO stage order (decided 2026-05-05):
//   1 Triagem → 2 Conferir Trash → 3 Sobreposição → 4 Classificação
//   → 5 Marcação Manual → 6 Conferência
// Internal IDs are kept stable across the rename (recuperar still backs
// "Conferir Trash") so we don't churn old data + tests; only label and
// position change. "controle" (5B Controle Sem Marca) is removed —
// items without SKU now ship through with empty class per the relaxed
// Lock 2.
type Stage =
  | "triagem"
  | "recuperar"
  | "sobreposicoes"
  | "classificacao"
  | "manuais"
  | "conferencia";

const STAGE_LABELS: Record<Stage, string> = {
  triagem: "1. Triagem",
  recuperar: "2. Conferir Trash",
  sobreposicoes: "3. Sobreposições",
  classificacao: "4. Classificação",
  manuais: "5. Marcação Manual",
  conferencia: "6. Conferência",
};

// All marcador stages live now. Future-only pills go here when needed.
const FUTURE_STAGES: { id: string; label: string }[] = [];

export function CycleReview() {
  const { cycleId = "" } = useParams();
  const [stage, setStage] = useState<Stage>("triagem");
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [selectedObjectId, setSelectedObjectId] = useState<string | null>(null);
  // Stage 3: SKU pre-selected before drawing. Held here so it persists
  // across draws — operators tag many boxes with the same SKU in a row.
  const [draftManualSku, setDraftManualSku] = useState<string | null>(null);
  // TagList hover → bbox highlight on canvas.
  const [hoveredTagId, setHoveredTagId] = useState<string | null>(null);
  // Stage 3 explicit move mode. When true and a tag is selected, the rect
  // fills with a transparent SKU-tinted background and the entire interior
  // is draggable. Resets whenever selection or stage changes.
  const [moveMode, setMoveMode] = useState(false);

  const queryClient = useQueryClient();
  const cycleKey = ["cycle", cycleId] as const;

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: cycleKey,
    queryFn: () => getCycle(cycleId),
    enabled: Boolean(cycleId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      const terminal = status === "COMPLETED" || status === "FAILED";
      if (terminal) return false;
      return status === "READY_FOR_REVIEW" || status === "IN_REVIEW"
        ? IDLE_REFETCH_MS
        : ACTIVE_REFETCH_MS;
    },
  });

  const pages = useMemo(() => data?.document.pages ?? [], [data]);
  const allObjects = useMemo(() => data?.objects ?? [], [data]);
  const allPairs = useMemo(() => data?.overlapPairs ?? [], [data]);
  const allConflicts = useMemo(() => data?.manualTrashConflicts ?? [], [data]);
  // Element → product links (rev03 r3-1).
  const connections = useMemo(() => data?.connections ?? [], [data]);
  /** Connections grouped by the element they start from. */
  const connectionsByElement = useMemo(() => {
    const out = new Map<string, Connection[]>();
    for (const c of connections) {
      out.set(c.fromObjectId, [...(out.get(c.fromObjectId) ?? []), c]);
    }
    return out;
  }, [connections]);
  const activePage = pages[activePageIndex] ?? null;
  const activeObjects = useMemo(
    () => (activePage ? allObjects.filter((o) => o.page === activePage.index) : []),
    [activePage, allObjects],
  );
  const activePagePairs = useMemo(
    () => (activePage ? allPairs.filter((p) => p.page === activePage.index) : []),
    [activePage, allPairs],
  );
  // Active overlap pair: first pending pair on the active page (auto-advances
  // through them as the operator resolves each).
  const activePair: OverlapPairWire | null = useMemo(
    () => activePagePairs.find((p) => p.decision === "pending") ?? null,
    [activePagePairs],
  );
  // #10: indexes (into `pages`) of every page that still has a pending pair.
  const pendingPairPageIndexes = useMemo(() => {
    const withPending = new Set(
      allPairs.filter((p) => p.decision === "pending").map((p) => p.page),
    );
    const out: number[] = [];
    pages.forEach((p, i) => {
      if (withPending.has(p.index)) out.push(i);
    });
    return out;
  }, [allPairs, pages]);
  // Per-stage canvas filter:
  // - Triagem: solo the selected tag so the operator decides útil/trash
  //   without visual noise.
  // - Conferir Trash (recuperar): show only trash items by default; solo
  //   the selected one if any (matches the side-panel list semantics).
  // - Classificação, Marcação Manual (manuais) and Conferência: hide
  //   suggested + trash so the operator only sees what's already an output
  //   of the cycle (confirmed/manual/exception). For Classificação this also
  //   fixes trash items reappearing on the canvas (Feedback V2 rev02 r2-7).
  // Other stages render every active tag.
  const canvasObjects = useMemo(() => {
    if (stage === "triagem" && selectedObjectId) {
      return activeObjects.filter((o) => o.id === selectedObjectId);
    }
    // Sobreposições: show ONLY the two marks of the active pair. Rendering
    // every mark on the page made it impossible to tell which boxes the A/B
    // decision actually referred to (Feedback V2 #11).
    if (stage === "sobreposicoes" && activePair) {
      return activeObjects.filter(
        (o) => o.id === activePair.objectAId || o.id === activePair.objectBId,
      );
    }
    if (stage === "recuperar") {
      if (selectedObjectId) {
        return activeObjects.filter((o) => o.id === selectedObjectId);
      }
      return activeObjects.filter((o) => o.status === "trash");
    }
    if (stage === "classificacao" || stage === "manuais" || stage === "conferencia") {
      return activeObjects.filter(
        (o) =>
          o.status === "confirmed" ||
          o.status === "manual" ||
          o.status === "exception",
      );
    }
    return activeObjects;
  }, [stage, selectedObjectId, activeObjects, activePair]);

  // ---------- Triagem mutation ----------
  const setStatusMutation = useMutation({
    mutationFn: ({ objectId, status }: { objectId: string; status: ObjectStatus }) =>
      patchObject(cycleId, objectId, { status }),
    onMutate: async ({ objectId, status }) => {
      await queryClient.cancelQueries({ queryKey: cycleKey });
      const prev = queryClient.getQueryData<CycleAnnotations>(cycleKey);
      if (prev) {
        queryClient.setQueryData<CycleAnnotations>(cycleKey, {
          ...prev,
          status: prev.status === "READY_FOR_REVIEW" ? "IN_REVIEW" : prev.status,
          objects: prev.objects.map((o) =>
            o.id === objectId ? { ...o, status } : o,
          ),
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(cycleKey, ctx.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: cycleKey });
    },
  });

  // ---------- Sobreposições mutation ----------
  const resolvePairMutation = useMutation({
    mutationFn: ({ pairId, decision }: { pairId: string; decision: OverlapDecision }) =>
      resolveOverlapPair(cycleId, pairId, decision),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: cycleKey });
    },
  });

  // ---------- Operational state (rev03 r3-3) ----------
  const operationalStateMutation = useMutation({
    mutationFn: (state: OperationalState) => setOperationalState(cycleId, state),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: cycleKey });
      queryClient.invalidateQueries({ queryKey: ["cycles"] });
    },
  });

  // ---------- Element → product links (rev03 r3-1) ----------
  const linkMutation = useMutation({
    mutationFn: ({ from, to }: { from: string; to: string }) =>
      createConnection(cycleId, from, to),
    onSettled: () => queryClient.invalidateQueries({ queryKey: cycleKey }),
  });
  const unlinkMutation = useMutation({
    mutationFn: (connectionId: string) => deleteConnection(cycleId, connectionId),
    onSettled: () => queryClient.invalidateQueries({ queryKey: cycleKey }),
  });

  // ---------- Recuperar Trash mutation (Stage 3B) ----------
  const resolveManualTrashMutation = useMutation({
    mutationFn: ({
      conflictId,
      decision,
    }: {
      conflictId: string;
      decision: ManualTrashDecision;
    }) => resolveManualTrashConflict(cycleId, conflictId, decision),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: cycleKey });
    },
  });

  // ---------- Stage 4 (Conferência) summary + lock ----------
  const lock1SummaryQuery = useQuery({
    queryKey: ["cycle", cycleId, "lock1-summary"] as const,
    queryFn: () => getLock1Summary(cycleId),
    enabled: Boolean(cycleId) && stage === "conferencia",
  });

  const lock1Mutation = useMutation({
    mutationFn: () => postLock1(cycleId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: cycleKey });
      queryClient.invalidateQueries({ queryKey: ["cycle", cycleId, "lock1-summary"] });
    },
  });

  // ---------- Stage 5 (Classificação) summary + lock ----------
  const lock2SummaryQuery = useQuery({
    queryKey: ["cycle", cycleId, "lock2-summary"] as const,
    queryFn: () => getLock2Summary(cycleId),
    enabled: Boolean(cycleId) && stage === "classificacao",
  });

  const lock2Mutation = useMutation({
    mutationFn: () => postLock2(cycleId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: cycleKey });
      queryClient.invalidateQueries({ queryKey: ["cycle", cycleId, "lock2-summary"] });
    },
  });

  // ---------- Manual object create / delete (Stage 3) ----------
  const createManualMutation = useMutation({
    mutationFn: ({
      page,
      bbox,
      classLabel,
    }: {
      page: number;
      bbox: BBox;
      classLabel?: string | null;
    }) => createManualObject(cycleId, { page, bbox, classLabel }),
    onSuccess: (created) => {
      // Select the box the operator just drew so the corner handles appear
      // immediately and Esc / Delete shortcuts work without an extra click.
      setSelectedObjectId(created.id);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: cycleKey });
    },
  });

  const moveObjectMutation = useMutation({
    mutationFn: ({ objectId, bbox }: { objectId: string; bbox: BBox }) =>
      patchObject(cycleId, objectId, { bbox }),
    onMutate: async ({ objectId, bbox }) => {
      await queryClient.cancelQueries({ queryKey: cycleKey });
      const prev = queryClient.getQueryData<CycleAnnotations>(cycleKey);
      if (prev) {
        queryClient.setQueryData<CycleAnnotations>(cycleKey, {
          ...prev,
          objects: prev.objects.map((o) =>
            o.id === objectId ? { ...o, bbox } : o,
          ),
        });
      }
      return { prev };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(cycleKey, ctx.prev);
      // Surface the failure so silent 4xx/5xx don't masquerade as
      // "released and nothing happened" — the rect snaps back to its old
      // position because the PATCH was rejected, and without this the
      // operator has no way to know why.
      console.error("moveObject failed", err);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: cycleKey });
    },
  });

  const deleteObjectMutation = useMutation({
    mutationFn: (objectId: string) => deleteObject(cycleId, objectId),
    onMutate: async (objectId) => {
      await queryClient.cancelQueries({ queryKey: cycleKey });
      const prev = queryClient.getQueryData<CycleAnnotations>(cycleKey);
      if (prev) {
        queryClient.setQueryData<CycleAnnotations>(cycleKey, {
          ...prev,
          objects: prev.objects.filter((o) => o.id !== objectId),
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(cycleKey, ctx.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: cycleKey });
    },
  });

  // ---------- Class label mutation ----------
  const setLabelMutation = useMutation({
    mutationFn: ({ objectId, classLabel }: { objectId: string; classLabel: string | null }) =>
      patchObject(cycleId, objectId, { classLabel }),
    onMutate: async ({ objectId, classLabel }) => {
      await queryClient.cancelQueries({ queryKey: cycleKey });
      const prev = queryClient.getQueryData<CycleAnnotations>(cycleKey);
      if (prev) {
        queryClient.setQueryData<CycleAnnotations>(cycleKey, {
          ...prev,
          objects: prev.objects.map((o) =>
            o.id === objectId ? { ...o, class: classLabel } : o,
          ),
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(cycleKey, ctx.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: cycleKey });
      queryClient.invalidateQueries({ queryKey: ["cycle", cycleId, "lock2-summary"] });
    },
  });

  // ---------- Batch class label (Feedback V2 #12) ----------
  // Assign one SKU to many objects at once so classification doesn't require
  // a click per bbox. Optimistic across all targets; PATCHes fire in parallel.
  const batchLabelMutation = useMutation({
    mutationFn: async ({
      objectIds,
      classLabel,
    }: {
      objectIds: string[];
      classLabel: string | null;
    }) => {
      await Promise.all(
        objectIds.map((id) => patchObject(cycleId, id, { classLabel })),
      );
    },
    onMutate: async ({ objectIds, classLabel }) => {
      await queryClient.cancelQueries({ queryKey: cycleKey });
      const prev = queryClient.getQueryData<CycleAnnotations>(cycleKey);
      if (prev) {
        const ids = new Set(objectIds);
        queryClient.setQueryData<CycleAnnotations>(cycleKey, {
          ...prev,
          objects: prev.objects.map((o) =>
            ids.has(o.id) ? { ...o, class: classLabel } : o,
          ),
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(cycleKey, ctx.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: cycleKey });
      queryClient.invalidateQueries({ queryKey: ["cycle", cycleId, "lock2-summary"] });
    },
  });

  const isLocked1 = data?.locked1At != null;
  const isLocked2 = data?.locked2At != null;

  // ---------- Triagem keyboard ----------
  // Triage history: every U/T/R push records what the object's status
  // was BEFORE the change so Ctrl+Z (or the "Desfazer" button) can roll
  // back N decisions in reverse order. Cleared when the stage changes
  // away from Triagem to keep the stack relevant.
  type TriageEntry = {
    objectId: string;
    prevStatus: ObjectStatus;
    pageIndex: number;
  };
  const TRIAGE_HISTORY_LIMIT = 50;
  const [triageHistory, setTriageHistory] = useState<TriageEntry[]>([]);
  useEffect(() => {
    if (stage !== "triagem") setTriageHistory([]);
  }, [stage]);

  const pushTriageHistory = useCallback(
    (entry: TriageEntry) => {
      setTriageHistory((prev) => {
        const next = [...prev, entry];
        if (next.length > TRIAGE_HISTORY_LIMIT) {
          next.shift();
        }
        return next;
      });
    },
    [],
  );

  const setStatus = useCallback(
    (status: ObjectStatus) => {
      if (!selectedObjectId) return;
      const target = activeObjects.find((o) => o.id === selectedObjectId);
      if (!target || target.status === status) return;
      pushTriageHistory({
        objectId: selectedObjectId,
        prevStatus: target.status,
        pageIndex: activePageIndex,
      });
      setStatusMutation.mutate({ objectId: selectedObjectId, status });
    },
    [
      selectedObjectId,
      activeObjects,
      activePageIndex,
      pushTriageHistory,
      setStatusMutation,
    ],
  );

  // Same as setStatus, but also moves selection to the next 'suggested'
  // object — first on the current page, then the first 'suggested' on
  // any subsequent page. If nothing is pending anywhere, deselect.
  const advanceTriage = useCallback(
    (status: ObjectStatus) => {
      if (!selectedObjectId) return;
      const target = activeObjects.find((o) => o.id === selectedObjectId);
      if (!target || target.status === status) return;
      pushTriageHistory({
        objectId: selectedObjectId,
        prevStatus: target.status,
        pageIndex: activePageIndex,
      });
      setStatusMutation.mutate({ objectId: selectedObjectId, status });
      const nextOnPage = activeObjects.find(
        (o) => o.id !== selectedObjectId && o.status === "suggested",
      );
      if (nextOnPage) {
        setSelectedObjectId(nextOnPage.id);
        return;
      }
      for (let i = activePageIndex + 1; i < pages.length; i++) {
        const pageIdx = pages[i].index;
        const next = allObjects.find(
          (o) => o.page === pageIdx && o.status === "suggested",
        );
        if (next) {
          setActivePageIndex(i);
          setSelectedObjectId(next.id);
          return;
        }
      }
      setSelectedObjectId(null);
    },
    [
      selectedObjectId,
      setStatusMutation,
      activeObjects,
      activePageIndex,
      pages,
      allObjects,
      pushTriageHistory,
    ],
  );

  // Ctrl/Cmd+Z (or the "Desfazer" button) reverses the most recent
  // triage decision: pop the last entry, navigate back to its page if
  // needed, re-select the object, and PATCH it back to its prior status.
  // Repeated calls walk back through the history.
  const undoTriage = useCallback(() => {
    setTriageHistory((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      // Re-locate the page index in case `pages` reordered (rare).
      const targetPageIdx = pages.findIndex((p) => p.index === last.pageIndex);
      if (targetPageIdx >= 0 && targetPageIdx !== activePageIndex) {
        setActivePageIndex(targetPageIdx);
      }
      setSelectedObjectId(last.objectId);
      setStatusMutation.mutate({
        objectId: last.objectId,
        status: last.prevStatus,
      });
      return prev.slice(0, -1);
    });
  }, [pages, activePageIndex, setStatusMutation]);

  // Sobreposições undo (Feedback V2 #13). Resolving a pair has object side
  // effects the backend does NOT auto-reverse when a pair is reset to
  // "pending" (keep_a trashes B; keep_b trashes A; merge creates a box AND
  // trashes both). So we capture the affected objects' prior statuses at
  // resolve time and replay the inverse on undo. Cleared on stage change.
  type OverlapUndoEntry = {
    pairId: string;
    decision: OverlapDecision;
    pageIndex: number;
    objAId: string;
    objAPrevStatus: ObjectStatus;
    objBId: string;
    objBPrevStatus: ObjectStatus;
  };
  const OVERLAP_HISTORY_LIMIT = 50;
  const [overlapHistory, setOverlapHistory] = useState<OverlapUndoEntry[]>([]);
  useEffect(() => {
    if (stage !== "sobreposicoes") setOverlapHistory([]);
  }, [stage]);

  const resolvePair = useCallback(
    (decision: OverlapDecision) => {
      if (!activePair) return;
      // Record an undo entry for every real decision (not for a "pending"
      // reset, which is itself the undo path).
      if (decision !== "pending") {
        const a = allObjects.find((o) => o.id === activePair.objectAId);
        const b = allObjects.find((o) => o.id === activePair.objectBId);
        setOverlapHistory((prev) => {
          const next = [
            ...prev,
            {
              pairId: activePair.pairId,
              decision,
              pageIndex: activePageIndex,
              objAId: activePair.objectAId,
              objAPrevStatus: (a?.status ?? "suggested") as ObjectStatus,
              objBId: activePair.objectBId,
              objBPrevStatus: (b?.status ?? "suggested") as ObjectStatus,
            },
          ];
          if (next.length > OVERLAP_HISTORY_LIMIT) next.shift();
          return next;
        });
      }
      resolvePairMutation.mutate({ pairId: activePair.pairId, decision });
    },
    [activePair, resolvePairMutation, allObjects, activePageIndex],
  );

  // Reverse the most recent overlap decision: reset the pair to pending and
  // restore the objects the decision had trashed / the box a merge created.
  const undoOverlap = useCallback(() => {
    setOverlapHistory((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      const targetPageIdx = pages.findIndex((p) => p.index === last.pageIndex);
      if (targetPageIdx >= 0 && targetPageIdx !== activePageIndex) {
        setActivePageIndex(targetPageIdx);
      }
      // 1) Roll the pair back to pending (raw mutation — no new history).
      resolvePairMutation.mutate({ pairId: last.pairId, decision: "pending" });
      // 2) Undo the object side effects the backend left in place.
      if (last.decision === "keep_a") {
        setStatusMutation.mutate({
          objectId: last.objBId,
          status: last.objBPrevStatus,
        });
      } else if (last.decision === "keep_b") {
        setStatusMutation.mutate({
          objectId: last.objAId,
          status: last.objAPrevStatus,
        });
      } else if (last.decision === "merge") {
        setStatusMutation.mutate({
          objectId: last.objAId,
          status: last.objAPrevStatus,
        });
        setStatusMutation.mutate({
          objectId: last.objBId,
          status: last.objBPrevStatus,
        });
        // The merged box id is carried on the (still-resolved) pair until the
        // reset above lands; delete it so the merge leaves no orphan.
        const mergedId =
          allPairs.find((p) => p.pairId === last.pairId)?.mergedObjectId ?? null;
        if (mergedId) deleteObjectMutation.mutate(mergedId);
      }
      return prev.slice(0, -1);
    });
  }, [
    pages,
    activePageIndex,
    allPairs,
    resolvePairMutation,
    setStatusMutation,
    deleteObjectMutation,
  ]);

  // Active manual-trash conflict: first pending one on the active page.
  const activePageConflicts = useMemo(
    () =>
      activePage ? allConflicts.filter((c) => c.page === activePage.index) : [],
    [activePage, allConflicts],
  );
  const activeConflict: ManualTrashConflictWire | null = useMemo(
    () => activePageConflicts.find((c) => c.decision === "pending") ?? null,
    [activePageConflicts],
  );

  const resolveConflict = useCallback(
    (decision: ManualTrashDecision) => {
      if (!activeConflict) return;
      resolveManualTrashMutation.mutate({
        conflictId: activeConflict.conflictId,
        decision,
      });
    },
    [activeConflict, resolveManualTrashMutation],
  );

  // ---------- #8: jump to next pending item ----------
  // Forward search (from the current page, after the current selection, then
  // wrapping) for the next object matching a predicate.
  const findNextPending = useCallback(
    (predicate: (o: AnnotationObject) => boolean) => {
      for (let step = 0; step < pages.length; step++) {
        const pageIdx = (activePageIndex + step) % pages.length;
        const pageValue = pages[pageIdx].index;
        const objs = allObjects.filter((o) => o.page === pageValue && predicate(o));
        if (objs.length === 0) continue;
        if (step === 0 && selectedObjectId) {
          const curIdx = objs.findIndex((o) => o.id === selectedObjectId);
          const after = curIdx >= 0 ? objs[curIdx + 1] : objs[0];
          if (after) return { pageIndex: pageIdx, objectId: after.id };
          continue; // nothing after the selection here → keep scanning forward
        }
        return { pageIndex: pageIdx, objectId: objs[0].id };
      }
      return null;
    },
    [pages, allObjects, activePageIndex, selectedObjectId],
  );

  const pendingPredicateForStage = useCallback(
    (o: AnnotationObject): boolean => {
      if (stage === "recuperar") return o.status === "trash";
      if (stage === "classificacao")
        return needsLabel(o) && (o.class === null || o.class === "");
      return o.status === "suggested";
    },
    [stage],
  );

  // 'n' / "Próxima pendência": advance to the next pending item for the
  // CURRENT stage (next pending pair page in Sobreposições) without leaving
  // the stage.
  const goToNextPending = useCallback(() => {
    if (stage === "sobreposicoes") {
      const next =
        pendingPairPageIndexes.find((i) => i > activePageIndex) ??
        pendingPairPageIndexes.find((i) => i !== activePageIndex);
      if (next !== undefined) setActivePageIndex(next);
      return;
    }
    const hit = findNextPending(pendingPredicateForStage);
    if (!hit) return;
    if (hit.pageIndex !== activePageIndex) setActivePageIndex(hit.pageIndex);
    setSelectedObjectId(hit.objectId);
  }, [
    stage,
    pendingPairPageIndexes,
    activePageIndex,
    findNextPending,
    pendingPredicateForStage,
  ]);

  // Clicking a summary counter sends the operator to that work: the right
  // stage + the first page that has such an item (selection is then handled
  // by the per-stage entry effects).
  const firstPageWith = useCallback(
    (predicate: (o: AnnotationObject) => boolean) => {
      for (let i = 0; i < pages.length; i++) {
        const pv = pages[i].index;
        if (allObjects.some((o) => o.page === pv && predicate(o))) return i;
      }
      return -1;
    },
    [pages, allObjects],
  );

  const jumpToStagePending = useCallback(
    (target: "suggested" | "trash" | "overlap") => {
      if (target === "overlap") {
        setStage("sobreposicoes"); // stage-entry effect lands on the right page
        return;
      }
      if (target === "trash") {
        setStage("recuperar");
        const idx = firstPageWith((o) => o.status === "trash");
        if (idx >= 0) setActivePageIndex(idx);
        return;
      }
      setStage("triagem");
      const idx = firstPageWith((o) => o.status === "suggested");
      if (idx >= 0) setActivePageIndex(idx);
    },
    [firstPageWith],
  );

  // Navigate to an object's page and select it (used by the batch list).
  const goToObject = useCallback(
    (objectId: string) => {
      const o = allObjects.find((x) => x.id === objectId);
      if (!o) return;
      const idx = pages.findIndex((p) => p.index === o.page);
      if (idx >= 0) setActivePageIndex(idx);
      setSelectedObjectId(objectId);
    },
    [allObjects, pages],
  );

  // Classificação flow (#12): set a label, then jump to the next unlabeled
  // útil/manual so the operator can label continuously without re-clicking.
  // Clearing a label (null) deliberately stays put.
  const advanceClassification = useCallback(
    (objectId: string, classLabel: string | null) => {
      setLabelMutation.mutate({ objectId, classLabel });
      if (classLabel == null || classLabel === "") return;
      const hit = findNextPending(
        (o) =>
          o.id !== objectId &&
          needsLabel(o) &&
          (o.class === null || o.class === ""),
      );
      if (hit) {
        if (hit.pageIndex !== activePageIndex) setActivePageIndex(hit.pageIndex);
        setSelectedObjectId(hit.objectId);
      }
    },
    [setLabelMutation, findNextPending, activePageIndex],
  );

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) {
        return;
      }
      if (e.key === "Escape") {
        setSelectedObjectId(null);
        return;
      }
      // Page navigation works in any stage when not editing text.
      if (e.key === "PageDown" || e.key === "]") {
        if (activePageIndex < pages.length - 1) {
          setActivePageIndex(activePageIndex + 1);
          e.preventDefault();
        }
        return;
      }
      if (e.key === "PageUp" || e.key === "[") {
        if (activePageIndex > 0) {
          setActivePageIndex(activePageIndex - 1);
          e.preventDefault();
        }
        return;
      }
      // 'n' jumps to the next pending item for the current stage (#8).
      if (e.key === "n" || e.key === "N") {
        goToNextPending();
        e.preventDefault();
        return;
      }
      // Lock 1 freezes object/overlap mutations; ignore those shortcuts.
      if (stage === "triagem" && !isLocked1) {
        // Ctrl/Cmd+Z reverses the last triage decision; works regardless
        // of selection (the undo will re-select the just-undone object).
        if ((e.ctrlKey || e.metaKey) && (e.key === "z" || e.key === "Z")) {
          undoTriage();
          e.preventDefault();
          return;
        }
        if (!selectedObjectId) return;
        if (e.key === "u" || e.key === "U") {
          advanceTriage("confirmed");
          e.preventDefault();
        } else if (e.key === "t" || e.key === "T") {
          advanceTriage("trash");
          e.preventDefault();
        } else if (e.key === "r" || e.key === "R") {
          // R reverts the current decision back to 'suggested' — keep the
          // selection so the operator can re-decide on the same item.
          setStatus("suggested");
          e.preventDefault();
        }
      } else if (stage === "manuais" && !isLocked1) {
        if (
          (e.key === "Delete" || e.key === "Backspace") &&
          selectedObjectId &&
          allObjects.find((o) => o.id === selectedObjectId)?.source === "manual"
        ) {
          deleteObjectMutation.mutate(selectedObjectId);
          setSelectedObjectId(null);
          e.preventDefault();
        } else if ((e.key === "m" || e.key === "M") && selectedObjectId) {
          setMoveMode((on) => !on);
          e.preventDefault();
        } else if (selectedObjectId && (e.key === "t" || e.key === "T")) {
          // rev03 r3-9: discard a wrong marking in place.
          setStatus("trash");
          e.preventDefault();
        } else if (selectedObjectId && (e.key === "u" || e.key === "U")) {
          setStatus("confirmed");
          e.preventDefault();
        }
      } else if (
        (stage === "classificacao" || stage === "conferencia") &&
        !isLocked1 &&
        selectedObjectId
      ) {
        // rev03 r3-9: correct a wrong marking without going back to Triagem.
        if (e.key === "t" || e.key === "T") {
          setStatus("trash");
          e.preventDefault();
        } else if (e.key === "u" || e.key === "U") {
          setStatus("confirmed");
          e.preventDefault();
        }
      } else if (stage === "sobreposicoes" && !isLocked1) {
        // Ctrl/Cmd+Z reverses the last overlap decision — works even when the
        // current page has no pending pair left (the undo re-opens one).
        if ((e.ctrlKey || e.metaKey) && (e.key === "z" || e.key === "Z")) {
          undoOverlap();
          e.preventDefault();
          return;
        }
        if (!activePair) return;
        if (e.key === "1") {
          resolvePair("keep_a");
          e.preventDefault();
        } else if (e.key === "2") {
          resolvePair("keep_b");
          e.preventDefault();
        } else if (e.key === "3") {
          resolvePair("keep_both");
          e.preventDefault();
        } else if (e.key === "m" || e.key === "M") {
          resolvePair("merge");
          e.preventDefault();
        }
      } else if (stage === "recuperar" && !isLocked1) {
        if (!activeConflict) return;
        if (e.key === "1") {
          resolveConflict("manual_wins");
          e.preventDefault();
        } else if (e.key === "2") {
          resolveConflict("trash_wins");
          e.preventDefault();
        } else if (e.key === "3") {
          resolveConflict("both_kept");
          e.preventDefault();
        }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    stage,
    selectedObjectId,
    activePair,
    activeConflict,
    setStatus,
    advanceTriage,
    undoTriage,
    resolvePair,
    undoOverlap,
    resolveConflict,
    goToNextPending,
    isLocked1,
    allObjects,
    deleteObjectMutation,
    activePageIndex,
    pages,
  ]);

  // On page change (or first entering Triagem): auto-select the first
  // 'suggested' object on the new page so the operator can keep firing
  // U/T continuously without any extra clicks. Other stages just clear
  // the selection. Depends only on activePageIndex/stage — not on
  // activeObjects — so a subsequent data refetch doesn't yank focus
  // back to the first pending after the operator has moved on.
  useEffect(() => {
    if (stage === "triagem") {
      const firstPending = activeObjects.find((o) => o.status === "suggested");
      setSelectedObjectId(firstPending ? firstPending.id : null);
    } else if (stage === "classificacao") {
      // #12: land on the first item that still needs a label so the operator
      // can start classifying immediately (and keep flowing via advance).
      const firstUnlabeled = activeObjects.find(
        (o) => needsLabel(o) && (o.class === null || o.class === ""),
      );
      setSelectedObjectId(firstUnlabeled ? firstUnlabeled.id : null);
    } else {
      setSelectedObjectId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePageIndex, stage]);

  // When the active overlap pair flips, reset selection (the side panel
  // covers it).
  useEffect(() => {
    if (stage === "sobreposicoes") setSelectedObjectId(null);
  }, [activePair, stage]);

  // On entering the stage, land on the first page that has a pending pair.
  useEffect(() => {
    if (stage !== "sobreposicoes" || isLocked1) return;
    const onPage = activePagePairs.some((p) => p.decision === "pending");
    if (!onPage && pendingPairPageIndexes.length > 0) {
      setActivePageIndex(pendingPairPageIndexes[0]);
    }
    // Stage-entry only — intentionally not re-run as pairs are resolved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  // After the operator clears the LAST pending pair on the page they're
  // already on (count drops >0 → 0 for the same page), hop to the next page
  // that still has pairs. Manual navigation to an empty page is preserved
  // because that's a page CHANGE, not a same-page count drop.
  const overlapPrevRef = useRef<{ page: number; pending: number }>({
    page: -1,
    pending: 0,
  });
  useEffect(() => {
    if (stage !== "sobreposicoes" || isLocked1) {
      overlapPrevRef.current = { page: -1, pending: 0 };
      return;
    }
    const onPage = activePagePairs.filter((p) => p.decision === "pending").length;
    const prev = overlapPrevRef.current;
    overlapPrevRef.current = { page: activePageIndex, pending: onPage };
    if (prev.page === activePageIndex && prev.pending > 0 && onPage === 0) {
      const nextIdx =
        pendingPairPageIndexes.find((i) => i > activePageIndex) ??
        pendingPairPageIndexes.find((i) => i !== activePageIndex);
      if (nextIdx !== undefined) setActivePageIndex(nextIdx);
    }
  }, [stage, isLocked1, activePagePairs, activePageIndex, pendingPairPageIndexes]);

  // Move mode auto-engages on selection inside Stage 3 — selecting a tag
  // is the user's signal that they want to do something with it. The
  // toggle button can still turn it off (e.g. to draw a new rect inside
  // the selected one). Outside Stage 3, or with nothing selected, off.
  useEffect(() => {
    setMoveMode(selectedObjectId !== null && stage === "manuais");
  }, [selectedObjectId, stage]);

  const selectedObject =
    selectedObjectId ? allObjects.find((o) => o.id === selectedObjectId) ?? null : null;
  const totals = useMemo(() => countByStatus(allObjects), [allObjects]);
  const perPageCounts = useMemo(
    () => countObjectsByPage(allObjects, allPairs, allConflicts, pages),
    [allObjects, allPairs, allConflicts, pages],
  );
  const pendingPairCount = useMemo(
    () => allPairs.filter((p) => p.decision === "pending").length,
    [allPairs],
  );
  // Conferir Trash badge: total items currently in trash on the cycle.
  // Operator's "to revisit" pile — high counts hint at over-aggressive T
  // during Triagem.
  const pendingTrashCount = useMemo(
    () => allObjects.filter((o) => o.status === "trash").length,
    [allObjects],
  );

  // Stage 2 highlights the active overlap pair (A=blue, B=cyan).
  // Stage 3B highlights the active manual-trash pair (manual=orange, trash=gray).
  // TagList hover (any stage): bright intelligence-blue stroke at 5px.
  const overrides = useMemo<Record<string, CanvasOverride>>(() => {
    const base: Record<string, CanvasOverride> = {};
    if (stage === "sobreposicoes" && activePair) {
      base[activePair.objectAId] = {
        stroke: colors.intelligence,
        thickness: 4,
        dashed: false,
        badge: "A",
        badgeCorner: "tl",
      };
      base[activePair.objectBId] = {
        stroke: colors.overlapB,
        thickness: 4,
        dashed: false,
        badge: "B",
        badgeCorner: "tr",
      };
    } else if (stage === "recuperar" && activeConflict) {
      base[activeConflict.manualObjectId] = {
        stroke: colors.manual,
        thickness: 4,
        dashed: false,
      };
      base[activeConflict.trashObjectId] = {
        stroke: colors.neutral,
        thickness: 4,
        dashed: false,
      };
    }
    // rev03 r3-1: selecting a product highlights the elements linked to it (and
    // vice-versa), so the relationship is visible on the page, not just in the
    // panel. Only in the stages where linking happens.
    if (
      (stage === "manuais" || stage === "classificacao") &&
      selectedObjectId &&
      connections.length > 0
    ) {
      const related = new Set<string>();
      for (const c of connections) {
        if (c.toObjectId === selectedObjectId) related.add(c.fromObjectId);
        if (c.fromObjectId === selectedObjectId) related.add(c.toObjectId);
      }
      for (const id of related) {
        base[id] = { stroke: colors.manual, thickness: 3, dashed: true };
      }
    }
    if (hoveredTagId) {
      base[hoveredTagId] = {
        stroke: colors.intelligence,
        thickness: 5,
        dashed: false,
      };
    }
    return base;
  }, [stage, activePair, activeConflict, hoveredTagId, selectedObjectId, connections]);

  // Header: back-link + cycle title share a row to save vertical space.
  // Title prefers the human-readable name; the cycle id stays as a small
  // muted suffix so the operator can still copy it for support tickets.
  const cycleName = data?.name?.trim() || data?.cycleId || cycleId;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-base)" }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <Link
          to="/cycles"
          style={{
            color: colors.textMuted,
            fontSize: "var(--fs-ui)",
            textDecoration: "none",
            flex: "0 0 auto",
          }}
        >
          ← Ciclos
        </Link>
        <h2
          style={{
            fontSize: "var(--fs-title)",
            lineHeight: "var(--lh-title)",
            margin: 0,
            flex: 1,
            minWidth: 0,
          }}
        >
          {cycleName}
        </h2>
        {data && data.cycleId !== cycleName && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              color: colors.textMuted,
              fontSize: "var(--fs-ui)",
            }}
          >
            <span
              className="mono"
              title="ID do ciclo (copie ao reportar problemas)"
            >
              {data.cycleId}
            </span>
            <CopyButton value={data.cycleId} label="ID do ciclo" />
          </span>
        )}
        {data && (
          <OperationalStateSelect
            value={data.operationalState}
            onChange={(s) => operationalStateMutation.mutate(s)}
            busy={operationalStateMutation.isPending}
          />
        )}
      </div>

      {isLoading && <p>Carregando…</p>}
      {error && (
        <p style={{ color: colors.danger }}>
          {error instanceof ApiError && error.status === 404
            ? "Ciclo não encontrado."
            : `Falha ao carregar: ${String(error)}`}
        </p>
      )}

      {data && (
        <>
          <SummaryRow
            data={data}
            totals={totals}
            pendingPairs={pendingPairCount}
            pendingTrash={pendingTrashCount}
            isLocked1={isLocked1}
            isLocked2={isLocked2}
            onJump={jumpToStagePending}
          />
          {data.status === "QUEUED" || data.status === "PROCESSING" ? (
            <ProcessingPlaceholder status={data.status} />
          ) : data.status === "FAILED" ? (
            <FailedNotice />
          ) : pages.length === 0 ? (
            <p style={{ color: colors.textMuted }}>
              Nenhuma página extraída. Verifique o arquivo.
            </p>
          ) : (
            <>
              <StageSelector
                stage={stage}
                onChange={setStage}
                pendingPairs={pendingPairCount}
                pendingTrash={pendingTrashCount}
                isLocked1={isLocked1}
                isLocked2={isLocked2}
              />
              <ReviewLayout
                cycleId={cycleId}
                downloads={data.downloads}
                pages={pages}
                activePage={activePage}
                activeObjects={activeObjects}
                canvasObjects={canvasObjects}
                perPageCounts={perPageCounts}
                activeIndex={activePageIndex}
                selectedObjectId={selectedObjectId}
                selectedObject={selectedObject}
                onSelectPage={setActivePageIndex}
                onSelectObject={setSelectedObjectId}
                onSetStatus={setStatus}
                onUndoTriage={undoTriage}
                triageUndoableCount={triageHistory.length}
                onSetClassLabel={(objectId, classLabel) =>
                  setLabelMutation.mutate({ objectId, classLabel })
                }
                onClassify={advanceClassification}
                onBatchSetClassLabel={(objectIds, classLabel) =>
                  batchLabelMutation.mutate({ objectIds, classLabel })
                }
                onGoToObject={goToObject}
                onCreateManual={(bbox) => {
                  if (!activePage) return;
                  createManualMutation.mutate({
                    page: activePage.index,
                    bbox,
                    classLabel: draftManualSku,
                  });
                }}
                onMoveObject={(objectId, bbox) =>
                  moveObjectMutation.mutate({ objectId, bbox })
                }
                onDeleteObject={(objectId) => {
                  deleteObjectMutation.mutate(objectId);
                  if (selectedObjectId === objectId) setSelectedObjectId(null);
                }}
                isMutating={
                  setStatusMutation.isPending ||
                  resolvePairMutation.isPending ||
                  resolveManualTrashMutation.isPending ||
                  lock1Mutation.isPending ||
                  lock2Mutation.isPending ||
                  setLabelMutation.isPending ||
                  batchLabelMutation.isPending ||
                  createManualMutation.isPending ||
                  moveObjectMutation.isPending ||
                  deleteObjectMutation.isPending
                }
                stage={stage}
                activePair={activePair}
                onResolvePair={resolvePair}
                overlapBusy={resolvePairMutation.isPending || isFetching}
                onUndoOverlap={undoOverlap}
                overlapUndoableCount={overlapHistory.length}
                connectionsByElement={connectionsByElement}
                onLinkElement={(from, to) => linkMutation.mutate({ from, to })}
                onUnlinkConnection={(id) => unlinkMutation.mutate(id)}
                pageOverrides={overrides}
                isLocked1={isLocked1}
                isLocked2={isLocked2}
                lock1Summary={lock1SummaryQuery.data ?? null}
                lock1SummaryLoading={lock1SummaryQuery.isLoading}
                onLock1={() => lock1Mutation.mutate()}
                lock1Error={lock1Mutation.error}
                lock2Summary={lock2SummaryQuery.data ?? null}
                lock2SummaryLoading={lock2SummaryQuery.isLoading}
                onLock2={() => lock2Mutation.mutate()}
                lock2Error={lock2Mutation.error}
                allObjects={allObjects}
                draftManualSku={draftManualSku}
                onSetDraftManualSku={setDraftManualSku}
                hoveredTagId={hoveredTagId}
                onHoverTag={setHoveredTagId}
                moveMode={moveMode}
                onSetMoveMode={setMoveMode}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}

// ---------- Stage selector ----------

function StageSelector({
  stage,
  onChange,
  pendingTrash,
  pendingPairs,
  isLocked1,
  isLocked2,
}: {
  stage: Stage;
  onChange: (s: Stage) => void;
  pendingTrash: number;
  pendingPairs: number;
  isLocked1: boolean;
  isLocked2: boolean;
}) {
  return (
    <div
      role="tablist"
      aria-label="Etapas"
      style={{
        display: "flex",
        gap: 6,
        flexWrap: "wrap",
        padding: "var(--space-tight)",
        background: colors.panel,
        border: `1px solid ${colors.line}`,
        borderRadius: "var(--radius-md)",
      }}
    >
      <StageTab
        active={stage === "triagem"}
        onClick={() => onChange("triagem")}
        label={STAGE_LABELS.triagem}
      />
      <StageTab
        active={stage === "recuperar"}
        onClick={() => onChange("recuperar")}
        label={STAGE_LABELS.recuperar}
        badge={pendingTrash > 0 ? String(pendingTrash) : undefined}
      />
      <StageTab
        active={stage === "sobreposicoes"}
        onClick={() => onChange("sobreposicoes")}
        label={STAGE_LABELS.sobreposicoes}
        badge={pendingPairs > 0 ? String(pendingPairs) : undefined}
      />
      <StageTab
        active={stage === "classificacao"}
        onClick={() => onChange("classificacao")}
        label={STAGE_LABELS.classificacao}
        badge={isLocked2 ? "🔒" : undefined}
      />
      <StageTab
        active={stage === "manuais"}
        onClick={() => onChange("manuais")}
        label={STAGE_LABELS.manuais}
      />
      <StageTab
        active={stage === "conferencia"}
        onClick={() => onChange("conferencia")}
        label={STAGE_LABELS.conferencia}
        badge={isLocked1 ? "🔒" : undefined}
      />
      {FUTURE_STAGES.map((s) => (
        <StageTab key={s.id} active={false} onClick={() => {}} label={s.label} disabled />
      ))}
    </div>
  );
}

function StageTab({
  active,
  disabled = false,
  onClick,
  label,
  badge,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  label: string;
  badge?: string;
}) {
  const fg = disabled
    ? colors.textMuted
    : active
      ? colors.onPrimary
      : colors.text;
  const bg = active ? colors.primary : "transparent";
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      aria-disabled={disabled}
      disabled={disabled}
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 12px",
        background: bg,
        color: fg,
        border: "none",
        borderRadius: "var(--radius-pill)",
        fontSize: "var(--fs-ui)",
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {label}
      {badge && (
        <span
          style={{
            padding: "0 6px",
            borderRadius: "var(--radius-pill)",
            background: active ? "rgba(255,255,255,0.25)" : colors.danger,
            color: active ? colors.onPrimary : colors.onDanger,
            fontFamily: "var(--font-mono)",
            fontSize: 11,
          }}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

// ---------- Review layout ----------

type PerPageCount = {
  useful: number;
  trash: number;
  pending: number;
  pendingPairs: number;
  pendingConflicts: number;
};

function ReviewLayout({
  cycleId,
  downloads,
  pages,
  activePage,
  activeObjects,
  canvasObjects,
  perPageCounts,
  activeIndex,
  selectedObjectId,
  selectedObject,
  onSelectPage,
  onSelectObject,
  onSetStatus,
  onUndoTriage,
  triageUndoableCount,
  onSetClassLabel,
  onClassify,
  onBatchSetClassLabel,
  onGoToObject,
  onCreateManual,
  onMoveObject,
  onDeleteObject,
  isMutating,
  stage,
  activePair,
  onResolvePair,
  overlapBusy,
  onUndoOverlap,
  overlapUndoableCount,
  connectionsByElement,
  onLinkElement,
  onUnlinkConnection,
  pageOverrides,
  isLocked1,
  isLocked2,
  lock1Summary,
  lock1SummaryLoading,
  onLock1,
  lock1Error,
  lock2Summary,
  lock2SummaryLoading,
  onLock2,
  lock2Error,
  allObjects,
  draftManualSku,
  onSetDraftManualSku,
  hoveredTagId,
  onHoverTag,
  moveMode,
  onSetMoveMode,
}: {
  cycleId: string;
  downloads: CycleDownloads;
  pages: CycleAnnotations["document"]["pages"];
  activePage: CycleAnnotations["document"]["pages"][number] | null;
  /** All tags on the active page — used for the side panel and counters. */
  activeObjects: AnnotationObject[];
  /** What the canvas should actually render. Equals activeObjects in most
   *  stages; in Triagem with a selection, only the selected tag. */
  canvasObjects: AnnotationObject[];
  perPageCounts: PerPageCount[];
  activeIndex: number;
  selectedObjectId: string | null;
  selectedObject: AnnotationObject | null;
  onSelectPage: (idx: number) => void;
  onSelectObject: (id: string | null) => void;
  onSetStatus: (status: ObjectStatus) => void;
  onUndoTriage: () => void;
  triageUndoableCount: number;
  onSetClassLabel: (objectId: string, classLabel: string | null) => void;
  onClassify: (objectId: string, classLabel: string | null) => void;
  onBatchSetClassLabel: (objectIds: string[], classLabel: string | null) => void;
  onGoToObject: (objectId: string) => void;
  onCreateManual: (bbox: BBox) => void;
  onMoveObject: (objectId: string, bbox: BBox) => void;
  onDeleteObject: (objectId: string) => void;
  isMutating: boolean;
  stage: Stage;
  activePair: OverlapPairWire | null;
  onResolvePair: (decision: OverlapDecision) => void;
  /** True while an overlap decision is being saved or the next item is being
   *  refetched — drives the canvas loading overlay (rev02 r2-4). */
  overlapBusy: boolean;
  onUndoOverlap: () => void;
  overlapUndoableCount: number;
  /** Element→product links keyed by the element object id (rev03 r3-1). */
  connectionsByElement: Map<string, Connection[]>;
  onLinkElement: (elementId: string, productId: string) => void;
  onUnlinkConnection: (connectionId: string) => void;
  pageOverrides: Record<string, CanvasOverride>;
  isLocked1: boolean;
  isLocked2: boolean;
  lock1Summary: Lock1Summary | null;
  lock1SummaryLoading: boolean;
  onLock1: () => void;
  lock1Error: unknown;
  lock2Summary: Lock2Summary | null;
  lock2SummaryLoading: boolean;
  onLock2: () => void;
  lock2Error: unknown;
  allObjects: AnnotationObject[];
  draftManualSku: string | null;
  onSetDraftManualSku: (code: string | null) => void;
  hoveredTagId: string | null;
  onHoverTag: (id: string | null) => void;
  moveMode: boolean;
  onSetMoveMode: (on: boolean) => void;
}) {
  const drawMode = stage === "manuais" && !isLocked1;
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  // Where the canvas column starts in the viewport — measured from the
  // DOM rather than estimated from a fixed chrome budget so that header /
  // summary / stage-selector size changes (longer cycle name, extra
  // warnings, etc.) don't push the canvas below the fold and force the
  // operator to scroll. Re-measured on viewport resize and whenever
  // anything that could shift the canvas's position changes.
  const [containerTop, setContainerTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(
    typeof window !== "undefined" ? window.innerHeight : 0,
  );
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      setContainerWidth(Math.floor(entry.contentRect.width));
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    const onResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  useLayoutEffect(() => {
    if (!containerRef.current) return;
    setContainerTop(containerRef.current.getBoundingClientRect().top);
    // Re-fire whenever the layout above the canvas could change height.
  }, [stage, viewportHeight, activePage, isLocked1, isLocked2]);

  // Fit-to-screen toggle. Default OFF so the canvas fills the full width of
  // its column (and the operator scrolls vertically for tall pages) — the
  // page was rendering at <1/3 of the screen, which the review flow found
  // unworkable (Feedback V2 #5). Operators who prefer a no-scroll view can
  // still flip it on. Persisted in localStorage; key is versioned so the new
  // default takes effect for existing sessions.
  const [fitToScreen, setFitToScreen] = useState<boolean>(() => {
    if (typeof localStorage === "undefined") return false;
    const stored = localStorage.getItem("marcador.fitCanvas.v2");
    return stored === null ? false : stored === "true";
  });
  useEffect(() => {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("marcador.fitCanvas.v2", String(fitToScreen));
    }
  }, [fitToScreen]);

  // Collapsible page thumbnail rail (Feedback V2 #5): collapsing it hands the
  // reclaimed width to the canvas. Collapsed is now the DEFAULT (rev02 r2-14:
  // the page-nav box should start collapsed since other nav exists) — the key
  // is versioned so the new default takes effect for existing sessions too.
  const [railCollapsed, setRailCollapsed] = useState<boolean>(() => {
    if (typeof localStorage === "undefined") return true;
    const stored = localStorage.getItem("marcador.railCollapsed.v2");
    return stored === null ? true : stored === "true";
  });
  useEffect(() => {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("marcador.railCollapsed.v2", String(railCollapsed));
    }
  }, [railCollapsed]);

  // Conferência continuous scroll (Feedback V2 #16): stack every page so the
  // operator validates by scrolling instead of clicking page-by-page.
  const [continuousConf, setContinuousConf] = useState<boolean>(() => {
    if (typeof localStorage === "undefined") return false;
    return localStorage.getItem("marcador.confContinuous") === "true";
  });
  useEffect(() => {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("marcador.confContinuous", String(continuousConf));
    }
  }, [continuousConf]);
  const continuousActive = stage === "conferencia" && continuousConf;

  // When fitToScreen is on, cap the canvas so its rendered height (width *
  // page-aspect) fits the viewport minus the actual offset from the top
  // (containerTop) and a small bottom buffer for the page caption + page
  // padding. The buffer is the only fixed-size piece; everything above is
  // measured live so the canvas exactly fills the remaining viewport.
  const CANVAS_BOTTOM_BUFFER_PX = 64;
  const stageWidth = useMemo(() => {
    if (containerWidth === 0) return 0;
    if (!fitToScreen || !activePage) return containerWidth;
    const pageAspect = activePage.heightPt / Math.max(activePage.widthPt, 1);
    const heightBudget = Math.max(
      viewportHeight - containerTop - CANVAS_BOTTOM_BUFFER_PX,
      200,
    );
    const widthFromHeight = Math.floor(heightBudget / Math.max(pageAspect, 0.01));
    return Math.min(containerWidth, widthFromHeight);
  }, [containerWidth, containerTop, fitToScreen, activePage, viewportHeight]);

  return (
    <div
      style={{
        display: "grid",
        // Trim the side columns so the canvas gets the bulk of the width;
        // collapsing the rail (#5) reclaims even more.
        gridTemplateColumns: railCollapsed ? "40px 1fr 264px" : "208px 1fr 264px",
        gap: "var(--space-base)",
        alignItems: "start",
      }}
    >
      <ThumbnailRail
        cycleId={cycleId}
        pages={pages}
        activeIndex={activeIndex}
        onSelect={onSelectPage}
        perPageCounts={perPageCounts}
        collapsed={railCollapsed}
        onToggleCollapse={() => setRailCollapsed((on) => !on)}
      />
      <div
        ref={containerRef}
        style={{
          minWidth: 0,
          // Center the canvas horizontally when fit-to-screen shrinks it
          // narrower than the column (typical for portrait pages on wide
          // monitors). When toggled off the canvas spans the full column
          // and there's nothing to center.
          display: "flex",
          flexDirection: "column",
          alignItems: fitToScreen && !continuousActive ? "center" : "stretch",
          position: "relative",
        }}
      >
        {/* Immediate loading feedback after an overlap decision (rev02 r2-4):
            resolving a pair takes a beat to register + refetch the next item;
            without this the operator can't tell if the click landed. */}
        {stage === "sobreposicoes" && overlapBusy && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 5,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                background: "rgba(255,255,255,0.6)",
                color: colors.text,
                fontSize: "var(--fs-ui)",
                pointerEvents: "none",
              }}
            >
              <span className="spinner" />
              Carregando próximo item…
            </div>
          )}
        {continuousActive ? (
          <ContinuousPages
            cycleId={cycleId}
            pages={pages}
            allObjects={allObjects}
            stageWidth={stageWidth > 0 ? stageWidth : containerWidth}
          />
        ) : (
          activePage &&
          stageWidth > 0 && (
            <PageCanvas
              cycleId={cycleId}
              page={activePage}
              objects={canvasObjects}
              stageWidth={stageWidth}
              selectedObjectId={selectedObjectId}
              onSelect={onSelectObject}
              overrides={pageOverrides}
              drawMode={drawMode}
              canDraw={drawMode && draftManualSku !== null}
              moveMode={moveMode}
              tintAll={stage === "triagem"}
              onDrawComplete={onCreateManual}
              onObjectMoved={onMoveObject}
            />
          )
        )}
        {activePage && (
          <div
            style={{
              marginTop: "var(--space-tight)",
              fontSize: "var(--fs-ui)",
              color: colors.textMuted,
              fontFamily: "var(--font-mono)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {/* Always-visible page nav so the shortcuts are discoverable
                  (rev02 r2-15). Hidden in continuous-scroll where paging by
                  index doesn't apply. */}
              {!continuousActive && (
                <button
                  type="button"
                  onClick={() => activeIndex > 0 && onSelectPage(activeIndex - 1)}
                  disabled={activeIndex <= 0}
                  title="Página anterior (PageUp ou [)"
                  aria-label="Página anterior"
                  style={pageNavBtn(activeIndex <= 0)}
                >
                  ‹
                </button>
              )}
              <span>
                {continuousActive
                  ? `${pages.length} página(s) · rolagem contínua`
                  : `página ${activePage.index + 1} / ${pages.length} · ${activeObjects.length} objeto(s) · ${activePage.widthPt.toFixed(0)} × ${activePage.heightPt.toFixed(0)} pt`}
              </span>
              {!continuousActive && (
                <button
                  type="button"
                  onClick={() =>
                    activeIndex < pages.length - 1 && onSelectPage(activeIndex + 1)
                  }
                  disabled={activeIndex >= pages.length - 1}
                  title="Próxima página (PageDown ou ])"
                  aria-label="Próxima página"
                  style={pageNavBtn(activeIndex >= pages.length - 1)}
                >
                  ›
                </button>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {stage === "conferencia" && (
                <button
                  type="button"
                  onClick={() => setContinuousConf((on) => !on)}
                  aria-pressed={continuousConf}
                  title={
                    continuousConf
                      ? "Rolagem contínua ativa — clique para página única"
                      : "Página única — clique para rolagem contínua"
                  }
                  style={{
                    padding: "0 10px",
                    height: 28,
                    background: continuousConf ? colors.primary : colors.panel,
                    color: continuousConf ? colors.onPrimary : colors.text,
                    border: `1px solid ${continuousConf ? colors.primary : colors.line}`,
                    borderRadius: "var(--radius-sm)",
                    cursor: "pointer",
                    fontSize: "var(--fs-ui)",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <span aria-hidden>≡</span> Rolagem contínua
                </button>
              )}
            <button
              type="button"
              onClick={() => setFitToScreen((on) => !on)}
              aria-pressed={fitToScreen}
              aria-label={
                fitToScreen
                  ? "Ajustado à tela. Clique para tamanho real."
                  : "Tamanho real. Clique para ajustar à tela."
              }
              title={
                fitToScreen
                  ? "Ajustado à tela — clique para tamanho real"
                  : "Tamanho real — clique para ajustar à tela"
              }
              style={{
                width: 28,
                height: 28,
                padding: 0,
                background: fitToScreen ? colors.primary : colors.panel,
                color: fitToScreen ? colors.onPrimary : colors.text,
                border: `1px solid ${fitToScreen ? colors.primary : colors.line}`,
                borderRadius: "var(--radius-sm)",
                cursor: "pointer",
                fontSize: 16,
                lineHeight: 1,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span aria-hidden>⛶</span>
            </button>
            </div>
          </div>
        )}
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-base)",
          // Sticky column capped to the viewport. The scroll is NOT on the
          // whole column anymore (rev02 r2-8): the stage actions stay put at
          // the top and only the Tags panel below scrolls internally.
          position: "sticky",
          top: "var(--space-base)",
          maxHeight: "calc(100vh - 2 * var(--space-base))",
          overflow: "hidden",
        }}
      >
      <div
        style={{
          // The stage actions card. rev03 r3-5: the old `maxHeight: 55%` never
          // applied — a percentage max-height needs a definite parent height,
          // and the parent only has max-height, so the cap was ignored and a
          // tall panel (e.g. Conferência) pushed the Tags panel to ~0. Now it
          // shrinks via flex and scrolls internally instead, while the Tags
          // panel below keeps a minHeight floor so it's always usable.
          flex: "0 1 auto",
          minHeight: 0,
          overflowY: "auto",
        }}
      >
      {stage === "triagem" ? (
        <TriageSidePanel
          selectedObject={selectedObject}
          onSetStatus={onSetStatus}
          onUndo={onUndoTriage}
          undoableCount={triageUndoableCount}
          isMutating={isMutating}
          isLocked1={isLocked1}
        />
      ) : stage === "sobreposicoes" ? (
        <OverlapSidePanel
          activePair={activePair}
          onResolve={onResolvePair}
          onUndo={onUndoOverlap}
          undoableCount={overlapUndoableCount}
          isMutating={isMutating}
          isLocked1={isLocked1}
        />
      ) : stage === "manuais" ? (
        <ManuaisSidePanel
          allObjects={allObjects}
          selectedObject={selectedObject}
          onDeleteObject={onDeleteObject}
          onSetClassLabel={onSetClassLabel}
          isMutating={isMutating}
          isLocked1={isLocked1}
          draftManualSku={draftManualSku}
          onSetDraftManualSku={onSetDraftManualSku}
          moveMode={moveMode}
          onSetMoveMode={onSetMoveMode}
        />
      ) : stage === "recuperar" ? (
        <ConferirTrashSidePanel
          activeObjects={activeObjects}
          selectedObject={selectedObject}
          onSelectObject={onSelectObject}
          onSetStatus={onSetStatus}
          isMutating={isMutating}
          isLocked1={isLocked1}
        />
      ) : stage === "conferencia" ? (
        <ConferenciaSidePanel
          cycleId={cycleId}
          downloads={downloads}
          summary={lock1Summary}
          summaryLoading={lock1SummaryLoading}
          isLocked1={isLocked1}
          isLocked2={isLocked2}
          isMutating={isMutating}
          onLock1={onLock1}
          lock1Error={lock1Error}
        />
      ) : (
        <ClassificacaoSidePanel
          selectedObject={selectedObject}
          allObjects={allObjects}
          summary={lock2Summary}
          summaryLoading={lock2SummaryLoading}
          isLocked2={isLocked2}
          isMutating={isMutating}
          onSetClassLabel={onClassify}
          onBatchSetClassLabel={onBatchSetClassLabel}
          onGoToObject={onGoToObject}
          onLock2={onLock2}
          lock2Error={lock2Error}
        />
      )}
      {(stage === "classificacao" || stage === "manuais" || stage === "conferencia") && (
        <QuickCorrectionBlock
          selectedObject={selectedObject}
          onSetStatus={onSetStatus}
          isMutating={isMutating}
          isLocked1={isLocked1}
        />
      )}
      {(stage === "manuais" || stage === "classificacao") && (
        <ProductLinkPanel
          selectedObject={selectedObject}
          activeObjects={activeObjects}
          connectionsByElement={connectionsByElement}
          onLink={onLinkElement}
          onUnlink={onUnlinkConnection}
          onSelectObject={onSelectObject}
          isMutating={isMutating}
          isLocked1={isLocked1}
        />
      )}
      </div>
        <SidePanel title="Tags da página" scrollBody>
          <TagList
            objects={activeObjects}
            hoveredTagId={hoveredTagId}
            selectedObjectId={selectedObjectId}
            onHover={onHoverTag}
            onSelect={onSelectObject}
          />
        </SidePanel>
      </div>
    </div>
  );
}

// ---------- Stage 1 side panel ----------

function TriageSidePanel({
  selectedObject,
  onSetStatus,
  onUndo,
  undoableCount,
  isMutating,
  isLocked1,
}: {
  selectedObject: AnnotationObject | null;
  onSetStatus: (status: ObjectStatus) => void;
  onUndo: () => void;
  undoableCount: number;
  isMutating: boolean;
  isLocked1: boolean;
}) {
  return (
    <SidePanel title="Triagem">
      {isLocked1 && <LockNotice />}
      {!selectedObject && (
        <p style={{ color: colors.textMuted, margin: 0 }}>
          Clique em uma caixa para selecioná-la, depois pressione{" "}
          <Kbd>U</Kbd> para útil, <Kbd>T</Kbd> para trash, <Kbd>R</Kbd> para
          devolver a sugerido, <Kbd>Esc</Kbd> para desmarcar.
          <br />
          <Kbd>Ctrl+Z</Kbd> desfaz a última decisão (até 50 níveis).
        </p>
      )}
      {!isLocked1 && (
        <ActionButton
          label={
            undoableCount > 0
              ? `↶ Desfazer (${undoableCount})`
              : "↶ Desfazer"
          }
          shortcut="Ctrl+Z"
          onClick={onUndo}
          disabled={isMutating || undoableCount === 0}
          tone="ghost"
        />
      )}

      {selectedObject && (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span
              className="mono"
              style={{ fontSize: "var(--fs-ui)", color: colors.textMuted }}
            >
              {selectedObject.id} · pág. {selectedObject.page + 1}
            </span>
            <span style={{ fontSize: "var(--fs-ui)" }}>
              estado atual:{" "}
              <strong style={{ color: colors.text }}>{selectedObject.status}</strong>
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <ActionButton
              label="✓ Útil"
              shortcut="U"
              onClick={() => onSetStatus("confirmed")}
              disabled={isLocked1 || isMutating || selectedObject.status === "confirmed"}
              tone="primary"
            />
            <ActionButton
              label="🗑 Trash"
              shortcut="T"
              onClick={() => onSetStatus("trash")}
              disabled={isLocked1 || isMutating || selectedObject.status === "trash"}
              tone="neutral"
            />
            <ActionButton
              label="↺ Voltar para sugerido"
              shortcut="R"
              onClick={() => onSetStatus("suggested")}
              disabled={isLocked1 || isMutating || selectedObject.status === "suggested"}
              tone="ghost"
            />
          </div>
        </>
      )}
    </SidePanel>
  );
}

// ---------- Stage 2 side panel ----------

function OverlapSidePanel({
  activePair,
  onResolve,
  onUndo,
  undoableCount,
  isMutating,
  isLocked1,
}: {
  activePair: OverlapPairWire | null;
  onResolve: (decision: OverlapDecision) => void;
  onUndo: () => void;
  undoableCount: number;
  isMutating: boolean;
  isLocked1: boolean;
}) {
  return (
    <SidePanel title="Sobreposições">
      {isLocked1 && <LockNotice />}
      {!isLocked1 && (
        <ActionButton
          label={undoableCount > 0 ? `↶ Desfazer (${undoableCount})` : "↶ Desfazer"}
          shortcut="Ctrl+Z"
          onClick={onUndo}
          disabled={isMutating || undoableCount === 0}
          tone="ghost"
        />
      )}
      {!activePair && (
        <p style={{ color: colors.textMuted, margin: 0 }}>
          Sem pares pendentes nesta página. As páginas sem sobreposição são
          puladas automaticamente; use <Kbd>Ctrl+Z</Kbd> para reverter uma
          decisão.
        </p>
      )}

      {activePair && (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "20px 1fr",
              rowGap: 6,
              fontSize: "var(--fs-ui)",
              alignItems: "center",
            }}
          >
            <Swatch color={colors.intelligence} />
            <span className="mono">
              <strong>A</strong> · {activePair.objectAId}
            </span>
            <Swatch color={colors.overlapB} />
            <span className="mono">
              <strong>B</strong> · {activePair.objectBId}
            </span>
            <span style={{ color: colors.textMuted }}>iou</span>
            <span className="mono" style={{ color: colors.text }}>
              {activePair.iou.toFixed(2)}
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <ActionButton
              label="Manter A"
              shortcut="1"
              onClick={() => onResolve("keep_a")}
              disabled={isLocked1 || isMutating}
              tone="primary"
              color={colors.intelligence}
              onColor={colors.onIntelligence}
              badge="A"
            />
            <ActionButton
              label="Manter B"
              shortcut="2"
              onClick={() => onResolve("keep_b")}
              disabled={isLocked1 || isMutating}
              tone="primary"
              color={colors.overlapB}
              onColor={colors.text}
              badge="B"
            />
            <ActionButton
              label="Manter ambos"
              shortcut="3"
              onClick={() => onResolve("keep_both")}
              disabled={isLocked1 || isMutating}
              tone="ghost"
            />
            <ActionButton
              label="Mesclar"
              shortcut="M"
              onClick={() => onResolve("merge")}
              disabled={isLocked1 || isMutating}
              tone="manual"
            />
          </div>
        </>
      )}
    </SidePanel>
  );
}

// ---------- Stage 3B side panel ----------

function ConferirTrashSidePanel({
  activeObjects,
  selectedObject,
  onSelectObject,
  onSetStatus,
  isMutating,
  isLocked1,
}: {
  activeObjects: AnnotationObject[];
  selectedObject: AnnotationObject | null;
  onSelectObject: (id: string | null) => void;
  onSetStatus: (status: ObjectStatus) => void;
  isMutating: boolean;
  isLocked1: boolean;
}) {
  const trashObjects = useMemo(
    () => activeObjects.filter((o) => o.status === "trash"),
    [activeObjects],
  );

  const selectedIdx = selectedObject
    ? trashObjects.findIndex((o) => o.id === selectedObject.id)
    : -1;

  // Step through the trash queue, soloing each item on the canvas (rev02
  // r2-12: pages with many trash items are hard to review all at once).
  const step = useCallback(
    (delta: number) => {
      if (trashObjects.length === 0) return;
      const cur = trashObjects.findIndex((o) => o.id === selectedObject?.id);
      const ni =
        cur < 0
          ? delta > 0
            ? 0
            : trashObjects.length - 1
          : (cur + delta + trashObjects.length) % trashObjects.length;
      onSelectObject(trashObjects[ni].id);
    },
    [trashObjects, selectedObject, onSelectObject],
  );

  // Apply a correction and auto-advance to the next still-trash item so the
  // operator flows through the queue without re-clicking the list.
  const decide = useCallback(
    (status: ObjectStatus) => {
      const cur = trashObjects.findIndex((o) => o.id === selectedObject?.id);
      const next =
        cur >= 0 ? (trashObjects[cur + 1] ?? trashObjects[cur - 1] ?? null) : null;
      onSetStatus(status);
      onSelectObject(next ? next.id : null);
    },
    [trashObjects, selectedObject, onSetStatus, onSelectObject],
  );

  // Keyboard queue control: ↑/↓ (or j/k) step; U/R correct the selected item.
  // The recuperar branch of the global handler only deals with manual-trash
  // conflicts, so these keys don't collide.
  useEffect(() => {
    if (isLocked1) return;
    function onKey(e: KeyboardEvent) {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) {
        return;
      }
      if (e.key === "ArrowDown" || e.key === "j") {
        step(1);
        e.preventDefault();
      } else if (e.key === "ArrowUp" || e.key === "k") {
        step(-1);
        e.preventDefault();
      } else if ((e.key === "u" || e.key === "U") && selectedIdx >= 0) {
        decide("confirmed");
        e.preventDefault();
      } else if ((e.key === "r" || e.key === "R") && selectedIdx >= 0) {
        decide("suggested");
        e.preventDefault();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isLocked1, step, decide, selectedIdx]);

  return (
    <SidePanel title="Conferir Trash">
      {isLocked1 && <LockNotice />}
      <p style={{ color: colors.textMuted, margin: 0, fontSize: "var(--fs-ui)" }}>
        Tudo que foi marcado como <strong>trash</strong> nesta página. Selecione
        para visualizar isolado no canvas; use os botões para corrigir.
        {trashObjects.length > 0 && (
          <>
            {" "}
            <Kbd>↑</Kbd>/<Kbd>↓</Kbd> navegam, <Kbd>U</Kbd>/<Kbd>R</Kbd> corrigem.
          </>
        )}
      </p>
      {trashObjects.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: "var(--fs-ui)",
            color: colors.textMuted,
          }}
        >
          <button
            type="button"
            onClick={() => step(-1)}
            disabled={trashObjects.length === 0}
            title="Item anterior (↑ ou k)"
            aria-label="Item anterior"
            style={pageNavBtn(trashObjects.length === 0)}
          >
            ‹
          </button>
          <span className="mono" style={{ flex: 1, textAlign: "center" }}>
            {selectedIdx >= 0
              ? `item ${selectedIdx + 1} / ${trashObjects.length}`
              : `${trashObjects.length} em trash`}
          </span>
          <button
            type="button"
            onClick={() => step(1)}
            disabled={trashObjects.length === 0}
            title="Próximo item (↓ ou j)"
            aria-label="Próximo item"
            style={pageNavBtn(trashObjects.length === 0)}
          >
            ›
          </button>
        </div>
      )}
      {trashObjects.length === 0 ? (
        <p
          style={{
            color: colors.textMuted,
            margin: 0,
            fontStyle: "italic",
            fontSize: "var(--fs-ui)",
          }}
        >
          Nenhum item em trash nesta página.
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {trashObjects.map((o) => {
            const isSelected = selectedObject?.id === o.id;
            return (
              <button
                key={o.id}
                type="button"
                onClick={() => onSelectObject(isSelected ? null : o.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "4px 6px",
                  background: isSelected ? colors.panelMuted : "transparent",
                  border: "none",
                  borderRadius: "var(--radius-sm)",
                  cursor: "pointer",
                  textAlign: "left",
                  fontSize: "var(--fs-ui)",
                  color: colors.text,
                }}
              >
                <Swatch color={colors.neutral} />
                <span className="mono" style={{ flex: 1 }}>
                  {o.id}
                </span>
                {o.class && (
                  <span style={{ color: colors.textMuted }} className="mono">
                    {o.class}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
      {selectedObject && selectedObject.status === "trash" && !isLocked1 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <ActionButton
            label="✓ Voltar a útil"
            shortcut="U"
            onClick={() => decide("confirmed")}
            disabled={isMutating}
            tone="primary"
          />
          <ActionButton
            label="↺ Voltar a sugerido"
            shortcut="R"
            onClick={() => decide("suggested")}
            disabled={isMutating}
            tone="ghost"
          />
        </div>
      )}
    </SidePanel>
  );
}

// ---------- In-context correction (rev03 r3-9) ----------

/** Fix a wrong marking where the operator notices it, instead of navigating
 *  back to Triagem to re-select the box and mark it trash (rev03 r3-9). */
function QuickCorrectionBlock({
  selectedObject,
  onSetStatus,
  isMutating,
  isLocked1,
}: {
  selectedObject: AnnotationObject | null;
  onSetStatus: (status: ObjectStatus) => void;
  isMutating: boolean;
  isLocked1: boolean;
}) {
  if (isLocked1 || !selectedObject) return null;
  const isTrash = selectedObject.status === "trash";
  return (
    <SidePanel title="Corrigir marcação">
      <p style={{ color: colors.textMuted, margin: 0, fontSize: "var(--fs-ui)" }}>
        <span className="mono">{selectedObject.class ?? selectedObject.id}</span>{" "}
        {isTrash
          ? "está em trash."
          : "está marcada como útil. Se estiver errada, descarte aqui mesmo — sem voltar para a Triagem."}
      </p>
      {isTrash ? (
        <ActionButton
          label="✓ Voltar a útil"
          shortcut="U"
          onClick={() => onSetStatus("confirmed")}
          disabled={isMutating}
          tone="primary"
        />
      ) : (
        <ActionButton
          label="🗑 Marcar como trash"
          shortcut="T"
          onClick={() => onSetStatus("trash")}
          disabled={isMutating}
          tone="neutral"
        />
      )}
      {/* rev03 r3-13: point at where geometry adjustments live. */}
      <span
        style={{
          color: colors.textMuted,
          fontSize: "var(--fs-ui)",
          fontStyle: "italic",
        }}
      >
        Para ajustar o recorte da caixa, use a etapa Marcação Manual.
      </span>
    </SidePanel>
  );
}

// ---------- Product↔element links (rev03 r3-1) ----------

/** Associate a secondary element (selo_desconto, casting, …) with the product
 *  it belongs to on the same page — the relationship V1 had and rev03 r3-1
 *  asks back for. Linking is optional: nothing here gates completion. */
function ProductLinkPanel({
  selectedObject,
  activeObjects,
  connectionsByElement,
  onLink,
  onUnlink,
  onSelectObject,
  isMutating,
  isLocked1,
}: {
  selectedObject: AnnotationObject | null;
  activeObjects: AnnotationObject[];
  connectionsByElement: Map<string, Connection[]>;
  onLink: (elementId: string, productId: string) => void;
  onUnlink: (connectionId: string) => void;
  onSelectObject: (id: string | null) => void;
  isMutating: boolean;
  isLocked1: boolean;
}) {
  const [draftProduct, setDraftProduct] = useState<string>("");

  // Products available as link targets: kept products on this page.
  const products = useMemo(
    () =>
      activeObjects.filter(
        (o) =>
          o.classKind === "produto" &&
          (o.status === "confirmed" || o.status === "manual" || o.status === "exception"),
      ),
    [activeObjects],
  );

  if (!selectedObject) {
    return (
      <SidePanel title="Produto ↔ elemento">
        <p style={{ color: colors.textMuted, margin: 0, fontSize: "var(--fs-ui)" }}>
          Selecione um elemento (selo, casting…) para associá-lo ao produto a que
          pertence, ou um produto para ver seus elementos.
        </p>
      </SidePanel>
    );
  }

  // A product: show what's attached to it.
  if (selectedObject.classKind === "produto") {
    const attached = activeObjects.filter((o) =>
      (connectionsByElement.get(o.id) ?? []).some(
        (c) => c.toObjectId === selectedObject.id,
      ),
    );
    return (
      <SidePanel title="Produto ↔ elemento">
        <p style={{ color: colors.textMuted, margin: 0, fontSize: "var(--fs-ui)" }}>
          Produto <span className="mono">{selectedObject.class}</span> —{" "}
          {attached.length === 0
            ? "nenhum elemento associado."
            : `${attached.length} elemento(s) associado(s):`}
        </p>
        {attached.map((el) => (
          <button
            key={el.id}
            type="button"
            onClick={() => onSelectObject(el.id)}
            style={linkRowStyle}
          >
            <span className="mono">{el.class ?? el.id}</span>
          </button>
        ))}
      </SidePanel>
    );
  }

  // Not classified yet — nothing to link.
  if (selectedObject.classKind !== "elemento") {
    return (
      <SidePanel title="Produto ↔ elemento">
        <p style={{ color: colors.textMuted, margin: 0, fontSize: "var(--fs-ui)" }}>
          Classifique esta caixa primeiro. Só elementos podem ser associados a um
          produto.
        </p>
      </SidePanel>
    );
  }

  const links = connectionsByElement.get(selectedObject.id) ?? [];
  const linkedIds = new Set(links.map((c) => c.toObjectId));
  const available = products.filter((p) => !linkedIds.has(p.id));

  return (
    <SidePanel title="Produto ↔ elemento">
      <p style={{ color: colors.textMuted, margin: 0, fontSize: "var(--fs-ui)" }}>
        Elemento <span className="mono">{selectedObject.class}</span>
        {links.length === 0 ? " — sem vínculo." : " — associado a:"}
      </p>

      {links.map((c) => {
        const product = activeObjects.find((o) => o.id === c.toObjectId);
        return (
          <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button
              type="button"
              onClick={() => onSelectObject(c.toObjectId)}
              style={{ ...linkRowStyle, flex: 1 }}
            >
              <span className="mono">{product?.class ?? c.toObjectId}</span>
            </button>
            {!isLocked1 && (
              <button
                type="button"
                onClick={() => onUnlink(c.id)}
                disabled={isMutating}
                title="Remover vínculo"
                aria-label="Remover vínculo"
                style={removeLinkStyle}
              >
                ✕
              </button>
            )}
          </div>
        );
      })}

      {isLocked1 ? (
        <LockNotice />
      ) : products.length === 0 ? (
        <p style={{ color: colors.textMuted, margin: 0, fontSize: "var(--fs-ui)" }}>
          Nenhum produto marcado nesta página para associar.
        </p>
      ) : (
        <div style={{ display: "flex", gap: 6 }}>
          <select
            value={draftProduct}
            onChange={(e) => setDraftProduct(e.target.value)}
            disabled={isMutating || available.length === 0}
            style={{
              flex: 1,
              minWidth: 0,
              padding: "6px 8px",
              borderRadius: "var(--radius-sm)",
              border: `1px solid ${colors.line}`,
              background: colors.panel,
              color: colors.text,
              fontSize: "var(--fs-ui)",
            }}
          >
            <option value="">
              {available.length === 0 ? "Já associado a todos" : "Escolha um produto…"}
            </option>
            {available.map((p) => (
              <option key={p.id} value={p.id}>
                {p.class}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => {
              if (!draftProduct) return;
              onLink(selectedObject.id, draftProduct);
              setDraftProduct("");
            }}
            disabled={isMutating || !draftProduct}
            style={{
              padding: "6px 12px",
              borderRadius: "var(--radius-sm)",
              border: "none",
              background: colors.primary,
              color: colors.onPrimary,
              fontWeight: 600,
              fontSize: "var(--fs-ui)",
              cursor: isMutating || !draftProduct ? "not-allowed" : "pointer",
              opacity: isMutating || !draftProduct ? 0.55 : 1,
            }}
          >
            Associar
          </button>
        </div>
      )}
    </SidePanel>
  );
}

const linkRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "4px 6px",
  background: colors.panelMuted,
  border: "none",
  borderRadius: "var(--radius-sm)",
  cursor: "pointer",
  textAlign: "left",
  fontSize: "var(--fs-ui)",
  color: colors.text,
};

const removeLinkStyle: React.CSSProperties = {
  flex: "none",
  width: 24,
  height: 24,
  border: `1px solid ${colors.line}`,
  borderRadius: "var(--radius-sm)",
  background: colors.panel,
  color: colors.textMuted,
  cursor: "pointer",
  fontSize: 12,
  lineHeight: 1,
};

// ---------- Stage 3 side panel ----------

function ManuaisSidePanel({
  allObjects,
  selectedObject,
  onDeleteObject,
  onSetClassLabel,
  isMutating,
  isLocked1,
  draftManualSku,
  onSetDraftManualSku,
  moveMode,
  onSetMoveMode,
}: {
  allObjects: AnnotationObject[];
  selectedObject: AnnotationObject | null;
  onDeleteObject: (objectId: string) => void;
  onSetClassLabel: (objectId: string, classLabel: string | null) => void;
  isMutating: boolean;
  isLocked1: boolean;
  draftManualSku: string | null;
  onSetDraftManualSku: (code: string | null) => void;
  moveMode: boolean;
  onSetMoveMode: (on: boolean) => void;
}) {
  const manualCount = allObjects.filter((o) => o.source === "manual").length;
  const isManual = selectedObject?.source === "manual";

  return (
    <SidePanel title="Ajustes Manuais">
      {isLocked1 && <LockNotice />}
      {!isLocked1 && (
        <>
          <p style={{ color: colors.textMuted, margin: 0, fontSize: "var(--fs-ui)" }}>
            Selecione um SKU abaixo, depois clique e arraste no canvas para
            desenhar uma caixa. A nova caixa já nasce com esse SKU — bom
            para rotular várias do mesmo produto em sequência.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: "var(--fs-ui)", color: colors.textMuted }}>
              SKU pré-selecionado
            </label>
            <SkuPicker
              value={draftManualSku}
              onChange={onSetDraftManualSku}
              disabled={isLocked1 || isMutating}
            />
            {!draftManualSku && (
              <span
                style={{ fontSize: "var(--fs-ui)", color: colors.warning }}
              >
                Selecione um SKU para habilitar o desenho. Sem SKU, novas
                caixas não podem ser criadas.
              </span>
            )}
          </div>
        </>
      )}

      <Stat
        label="Manuais nesta etapa"
        value={String(manualCount)}
        mono
        color={colors.manual}
      />

      {selectedObject && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span
            className="mono"
            style={{ fontSize: "var(--fs-ui)", color: colors.textMuted }}
          >
            {selectedObject.id} · pág. {selectedObject.page + 1}
          </span>
          <span style={{ fontSize: "var(--fs-ui)" }}>
            origem:{" "}
            <strong style={{ color: colors.text }}>{selectedObject.source}</strong>
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: "var(--fs-ui)", color: colors.textMuted }}>
              SKU desta caixa
            </label>
            <SkuPicker
              key={selectedObject.id}
              value={selectedObject.class}
              onChange={(code) => onSetClassLabel(selectedObject.id, code)}
              disabled={isLocked1 || isMutating}
              suggested={
                selectedObject.recommendedClassLabel &&
                selectedObject.recommendationConfidence
                  ? {
                      code: selectedObject.recommendedClassLabel,
                      confidence: selectedObject.recommendationConfidence,
                    }
                  : null
              }
            />
          </div>
          <ActionButton
            label={moveMode ? "Mover / ajustar: ativo" : "Mover / ajustar caixa"}
            shortcut="M"
            onClick={() => onSetMoveMode(!moveMode)}
            disabled={isLocked1 || isMutating}
            tone={moveMode ? "primary" : "neutral"}
          />
          {/* rev03 r3-13: the resize/crop affordance existed but wasn't
              discoverable — spell out that it lives here and how to reach it. */}
          <span
            style={{
              color: colors.textMuted,
              fontSize: "var(--fs-ui)",
              margin: 0,
              fontStyle: "italic",
            }}
          >
            {moveMode
              ? "Arraste a caixa preenchida para movê-la, ou as alças dos cantos para ajustar o recorte. Esc ou clique em outra caixa para sair."
              : "É aqui que se ajusta o recorte de uma bbox: selecione a caixa e ative Mover / ajustar (M) — aparecem alças nos cantos para redimensionar."}
          </span>
          {isManual ? (
            <ActionButton
              label="🗑 Excluir caixa manual"
              shortcut="Del"
              onClick={() => onDeleteObject(selectedObject.id)}
              disabled={isLocked1 || isMutating}
              tone="neutral"
            />
          ) : (
            <p
              style={{
                color: colors.textMuted,
                fontSize: "var(--fs-ui)",
                margin: 0,
                fontStyle: "italic",
              }}
            >
              Apenas caixas <span className="mono">manual_box</span> podem ser
              excluídas. Para descartar uma caixa extraída, mude o status para
              trash em <strong>Triagem</strong>.
            </p>
          )}
        </div>
      )}
    </SidePanel>
  );
}

// ---------- Stage 4 side panel ----------

function DownloadArtifactsBlock({
  cycleId,
  downloads,
}: {
  cycleId: string;
  downloads: CycleDownloads;
}) {
  return (
    <>
      <a
        href={preferredAssetUrl(cycleId, "annotated.pdf", downloads.annotatedPdfUrl)!}
        target="_blank"
        rel="noopener"
        download={`${cycleId}-annotated.pdf`}
        style={{
          padding: "10px 14px",
          background: colors.primary,
          color: colors.onPrimary,
          border: `1px solid ${colors.primary}`,
          borderRadius: "var(--radius-sm)",
          textDecoration: "none",
          textAlign: "center",
          fontSize: "var(--fs-body)",
          fontWeight: 600,
        }}
      >
        ⬇ Baixar PDF anotado
      </a>
      <a
        href={preferredAssetUrl(cycleId, "cycle.csv", downloads.cycleCsvUrl)!}
        target="_blank"
        rel="noopener"
        download={`${cycleId}.csv`}
        style={{
          padding: "10px 14px",
          background: colors.intelligence,
          color: colors.onIntelligence,
          border: `1px solid ${colors.intelligence}`,
          borderRadius: "var(--radius-sm)",
          textDecoration: "none",
          textAlign: "center",
          fontSize: "var(--fs-body)",
          fontWeight: 600,
        }}
      >
        ⬇ Baixar CSV
      </a>
      <a
        href={preferredAssetUrl(cycleId, "original.pdf", downloads.originalPdfUrl)!}
        target="_blank"
        rel="noopener"
        download={`${cycleId}-original.pdf`}
        style={{
          padding: "8px 14px",
          background: "transparent",
          color: colors.text,
          border: `1px solid ${colors.line}`,
          borderRadius: "var(--radius-sm)",
          textDecoration: "none",
          textAlign: "center",
          fontSize: "var(--fs-ui)",
        }}
      >
        Baixar PDF original
      </a>
    </>
  );
}


function ConferenciaSidePanel({
  cycleId,
  downloads,
  summary,
  summaryLoading,
  isLocked1,
  isLocked2,
  isMutating,
  onLock1,
  lock1Error,
}: {
  cycleId: string;
  downloads: CycleDownloads;
  summary: Lock1Summary | null;
  summaryLoading: boolean;
  isLocked1: boolean;
  isLocked2: boolean;
  isMutating: boolean;
  onLock1: () => void;
  lock1Error: unknown;
}) {
  const finalized = isLocked1 && isLocked2;
  return (
    <SidePanel title="Conferência">
      {isLocked1 ? (
        <>
          <LockBadge
            label={finalized ? "Ciclo concluído" : "Caixas concluídas"}
          />
          <p style={{ color: colors.textMuted, margin: 0, fontSize: "var(--fs-ui)" }}>
            {finalized
              ? "As decisões foram consolidadas e os artefatos foram gerados."
              : "Caixas consolidadas. Volte para Classificação para Salvar SKUs e finalizar o ciclo."}
          </p>
          {summary && <SummaryStats summary={summary} />}
          {finalized && <DownloadArtifactsBlock cycleId={cycleId} downloads={downloads} />}
        </>
      ) : summaryLoading ? (
        <p style={{ color: colors.textMuted, margin: 0 }}>Calculando…</p>
      ) : summary ? (
        <>
          <SummaryStats summary={summary} />

          {summary.blockedReasons.length > 0 && (
            <ReasonList reasons={summary.blockedReasons} tone="danger" />
          )}
          {summary.warnings.length > 0 && (
            <ReasonList reasons={summary.warnings} tone="warning" />
          )}

          {lock1Error instanceof Error && (
            <p style={{ color: colors.danger, margin: 0, fontSize: "var(--fs-ui)" }}>
              {lock1Error.message}
            </p>
          )}

          <button
            type="button"
            onClick={onLock1}
            disabled={!summary.canLock || isMutating}
            style={{
              padding: "12px 16px",
              background: summary.canLock ? colors.primary : colors.panelMuted,
              color: summary.canLock ? colors.onPrimary : colors.textMuted,
              border: "none",
              borderRadius: "var(--radius-sm)",
              cursor: summary.canLock && !isMutating ? "pointer" : "not-allowed",
              fontSize: "var(--fs-body)",
              fontWeight: 600,
              opacity: isMutating ? 0.55 : 1,
            }}
          >
            {isMutating ? "Concluindo…" : "🔒 Concluir Ciclo"}
          </button>
        </>
      ) : (
        <p style={{ color: colors.textMuted, margin: 0 }}>Sem dados.</p>
      )}
    </SidePanel>
  );
}

function SummaryStats({ summary }: { summary: Lock1Summary }) {
  return (
    <dl
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto",
        rowGap: 4,
        margin: 0,
        fontSize: "var(--fs-ui)",
      }}
    >
      <Term>Útil</Term>
      <Value mono color={colors.primary}>
        {summary.confirmed}
      </Value>
      <Term>Trash</Term>
      <Value mono color={colors.neutral}>
        {summary.trash}
      </Value>
      <Term>Manual</Term>
      <Value mono color={colors.manual}>
        {summary.manual}
      </Value>
      <Term>Pendente</Term>
      <Value mono color={summary.suggested > 0 ? colors.danger : colors.textMuted}>
        {summary.suggested}
      </Value>
      <Term>Sobreposições</Term>
      <Value mono color={summary.pendingOverlapPairs > 0 ? colors.danger : colors.textMuted}>
        {summary.pendingOverlapPairs}
      </Value>
      <Term>Memória</Term>
      <Value mono>{summary.objectMemoryWrites}</Value>
      <Term>Trash sigs.</Term>
      <Value mono>{summary.trashSignatureWrites}</Value>
    </dl>
  );
}

function Term({ children }: { children: React.ReactNode }) {
  return <dt style={{ color: colors.textMuted }}>{children}</dt>;
}

function Value({
  children,
  color,
  mono = false,
}: {
  children: React.ReactNode;
  color?: string;
  mono?: boolean;
}) {
  return (
    <dd
      style={{
        margin: 0,
        textAlign: "right",
        color: color ?? colors.text,
        fontFamily: mono ? "var(--font-mono)" : "var(--font-body)",
      }}
    >
      {children}
    </dd>
  );
}

function LockNotice() {
  return (
    <div
      role="note"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 10px",
        background: colors.panelMuted,
        borderRadius: "var(--radius-sm)",
        fontSize: "var(--fs-ui)",
        color: colors.text,
      }}
    >
      <span aria-hidden>🔒</span>
      <span>Stage 4 concluída — esta etapa é somente leitura.</span>
    </div>
  );
}

function LockBadge({ label }: { label: string }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px",
        background: colors.primary,
        color: colors.onPrimary,
        borderRadius: "var(--radius-pill)",
        fontSize: "var(--fs-ui)",
        fontWeight: 600,
        alignSelf: "flex-start",
      }}
    >
      <span aria-hidden>🔒</span> {label}
    </div>
  );
}

// ---------- Stage 5 side panel ----------

function ClassificacaoSidePanel({
  selectedObject,
  allObjects,
  summary,
  summaryLoading,
  isLocked2,
  isMutating,
  onSetClassLabel,
  onBatchSetClassLabel,
  onGoToObject,
  onLock2,
  lock2Error,
}: {
  selectedObject: AnnotationObject | null;
  allObjects: AnnotationObject[];
  summary: Lock2Summary | null;
  summaryLoading: boolean;
  isLocked2: boolean;
  isMutating: boolean;
  onSetClassLabel: (objectId: string, classLabel: string | null) => void;
  onBatchSetClassLabel: (objectIds: string[], classLabel: string | null) => void;
  onGoToObject: (objectId: string) => void;
  onLock2: () => void;
  lock2Error: unknown;
}) {
  return (
    <SidePanel title="Classificação">
      {isLocked2 && (
        <>
          <LockBadge label="SKUs salvos" />
          <p style={{ color: colors.textMuted, margin: 0, fontSize: "var(--fs-ui)" }}>
            Rótulos salvos na memória global. Vá para Conferência para
            concluir o ciclo e baixar PDF anotado / CSV.
          </p>
        </>
      )}

      {!isLocked2 && selectedObject && needsLabel(selectedObject) && (
        <ClassificationEditor
          selectedObject={selectedObject}
          isMutating={isMutating}
          onSetClassLabel={onSetClassLabel}
        />
      )}

      {!isLocked2 && (!selectedObject || !needsLabel(selectedObject)) && (
        <p style={{ color: colors.textMuted, margin: 0, fontSize: "var(--fs-ui)" }}>
          Selecione uma caixa <em>útil</em> no canvas para classificar. Itens em{" "}
          <span style={{ color: colors.neutral }}>trash</span> ou{" "}
          <span style={{ color: colors.intelligence }}>sugerido</span> não recebem
          rótulo nesta etapa.
        </p>
      )}

      {!isLocked2 && (
        <>
          {summaryLoading && !summary ? (
            <p style={{ color: colors.textMuted, margin: 0 }}>Calculando…</p>
          ) : summary ? (
            <Lock2SummaryStats summary={summary} />
          ) : null}

          {summary && summary.blockedReasons.length > 0 && (
            <ReasonList reasons={summary.blockedReasons} tone="danger" />
          )}
          {summary && summary.warnings.length > 0 && (
            <ReasonList reasons={summary.warnings} tone="warning" />
          )}

          {lock2Error instanceof Error && (
            <p style={{ color: colors.danger, margin: 0, fontSize: "var(--fs-ui)" }}>
              {lock2Error.message}
            </p>
          )}

          <button
            type="button"
            onClick={onLock2}
            disabled={!summary?.canLock || isMutating}
            style={{
              padding: "12px 16px",
              background: summary?.canLock ? colors.primary : colors.panelMuted,
              color: summary?.canLock ? colors.onPrimary : colors.textMuted,
              border: "none",
              borderRadius: "var(--radius-sm)",
              cursor: summary?.canLock && !isMutating ? "pointer" : "not-allowed",
              fontSize: "var(--fs-body)",
              fontWeight: 600,
              opacity: isMutating ? 0.55 : 1,
            }}
          >
            {isMutating ? "Salvando…" : "💾 Salvar SKUs"}
          </button>

          <BatchClassifyBlock
            allObjects={allObjects}
            selectedObjectId={selectedObject?.id ?? null}
            isMutating={isMutating}
            onGoToObject={onGoToObject}
            onBatchSetClassLabel={onBatchSetClassLabel}
          />
        </>
      )}
    </SidePanel>
  );
}

function needsLabel(o: AnnotationObject): boolean {
  return o.status === "confirmed" || o.status === "manual";
}

function ClassificationEditor({
  selectedObject,
  isMutating,
  onSetClassLabel,
}: {
  selectedObject: AnnotationObject;
  isMutating: boolean;
  onSetClassLabel: (objectId: string, classLabel: string | null) => void;
}) {
  const qc = useQueryClient();
  const { data: skus } = useSkuCatalog();
  const resolvedName = lookupSku(skus, selectedObject.class)?.name;
  const labelMissing = selectedObject.class !== null && resolvedName === undefined;
  const code = selectedObject.class ?? "";

  // Inline "Adicionar ao catálogo" form: when the object's class points at
  // a code that's not in v2_skus (typical of an auto-prefilled SKU detected
  // by the text suggester whose code never made it into the import), let
  // the operator name + register it without leaving the screen.
  const [pendingName, setPendingName] = useState("");
  useEffect(() => {
    setPendingName("");
  }, [selectedObject.id, selectedObject.class]);
  const registerMut = useMutation({
    mutationFn: (vars: { code: string; name: string }) =>
      createSku(vars.code, vars.name),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["skus"] });
      qc.invalidateQueries({ queryKey: ["skus-page"] });
      setPendingName("");
    },
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span
        className="mono"
        style={{ fontSize: "var(--fs-ui)", color: colors.textMuted }}
      >
        {selectedObject.id} · pág. {selectedObject.page + 1}
      </span>
      <label style={{ fontSize: "var(--fs-ui)", color: colors.textMuted }}>
        SKU / classe
      </label>
      <SkuPicker
        key={selectedObject.id}
        value={selectedObject.class}
        disabled={isMutating}
        autoFocus
        onChange={(c) => onSetClassLabel(selectedObject.id, c)}
        suggested={
          selectedObject.recommendedClassLabel &&
          selectedObject.recommendationConfidence
            ? {
                code: selectedObject.recommendedClassLabel,
                confidence: selectedObject.recommendationConfidence,
              }
            : null
        }
      />
      {labelMissing && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            padding: "8px 10px",
            background: "rgba(217,119,6,0.08)",
            border: `1px dashed ${colors.warning}`,
            borderRadius: "var(--radius-sm)",
          }}
        >
          <span style={{ fontSize: "var(--fs-ui)", color: colors.warning }}>
            <span className="mono">{code}</span> não está no catálogo —
            adicione um nome e cadastre.
          </span>
          <input
            type="text"
            value={pendingName}
            placeholder="nome do SKU"
            onChange={(e) => setPendingName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                registerMut.mutate({ code, name: pendingName.trim() });
              }
            }}
            disabled={registerMut.isPending}
            style={{
              padding: "6px 8px",
              fontFamily: "var(--font-mono)",
              fontSize: "var(--fs-ui)",
              border: `1px solid ${colors.line}`,
              borderRadius: "var(--radius-sm)",
              background: colors.panel,
              color: colors.text,
            }}
          />
          <button
            type="button"
            onClick={() => registerMut.mutate({ code, name: pendingName.trim() })}
            disabled={registerMut.isPending}
            style={{
              padding: "6px 10px",
              background: colors.warning,
              color: colors.onWarning,
              border: "none",
              borderRadius: "var(--radius-sm)",
              cursor: "pointer",
              fontSize: "var(--fs-ui)",
              fontWeight: 600,
            }}
          >
            {registerMut.isPending
              ? "Adicionando…"
              : `Adicionar ${code}${pendingName.trim() ? ` — ${pendingName.trim()}` : " (sem nome)"} ao catálogo`}
          </button>
        </div>
      )}
      {selectedObject.class && (
        <ActionButton
          label="Limpar"
          shortcut="⌫"
          onClick={() => onSetClassLabel(selectedObject.id, null)}
          disabled={isMutating}
          tone="ghost"
        />
      )}
    </div>
  );
}

function Lock2SummaryStats({ summary }: { summary: Lock2Summary }) {
  return (
    <dl
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto",
        rowGap: 4,
        margin: 0,
        fontSize: "var(--fs-ui)",
      }}
    >
      <Term>Para classificar</Term>
      <Value mono>{summary.needsLabel}</Value>
      <Term>Rotulados</Term>
      <Value mono color={colors.primary}>
        {summary.labeled}
      </Value>
      <Term>Faltando</Term>
      <Value mono color={summary.unlabeled > 0 ? colors.danger : colors.textMuted}>
        {summary.unlabeled}
      </Value>
      <Term>Memória de labels</Term>
      <Value mono>{summary.labelMemoryWrites}</Value>
    </dl>
  );
}

// Batch classification (#12): check several unlabeled útil/manual boxes and
// assign one SKU to all of them, instead of clicking each bbox. Each row is
// also a jump link to that object on the canvas for precision work.
function BatchClassifyBlock({
  allObjects,
  selectedObjectId,
  isMutating,
  onGoToObject,
  onBatchSetClassLabel,
}: {
  allObjects: AnnotationObject[];
  selectedObjectId: string | null;
  isMutating: boolean;
  onGoToObject: (objectId: string) => void;
  onBatchSetClassLabel: (objectIds: string[], classLabel: string | null) => void;
}) {
  const unlabeled = useMemo(
    () =>
      allObjects.filter(
        (o) => needsLabel(o) && (o.class === null || o.class === ""),
      ),
    [allObjects],
  );
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [batchSku, setBatchSku] = useState<string | null>(null);

  // Drop checks for ids that left the unlabeled set (got a label elsewhere).
  useEffect(() => {
    const valid = new Set(unlabeled.map((o) => o.id));
    setChecked((prev) => {
      let changed = false;
      const next = new Set<string>();
      prev.forEach((id) => {
        if (valid.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [unlabeled]);

  if (unlabeled.length === 0) {
    return (
      <p style={{ fontSize: "var(--fs-ui)", color: colors.primary, margin: 0 }}>
        Todos os itens úteis foram rotulados ✓
      </p>
    );
  }

  const allChecked = checked.size === unlabeled.length;
  const toggle = (id: string) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () =>
    setChecked(allChecked ? new Set() : new Set(unlabeled.map((o) => o.id)));
  const apply = () => {
    if (!batchSku || checked.size === 0) return;
    onBatchSetClassLabel(Array.from(checked), batchSku);
    setChecked(new Set());
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <span style={{ fontSize: "var(--fs-ui)", color: colors.textMuted }}>
          Faltam {unlabeled.length} · em lote ({checked.size})
        </span>
        <button
          type="button"
          onClick={toggleAll}
          style={{
            background: "transparent",
            border: "none",
            color: colors.intelligence,
            cursor: "pointer",
            fontSize: "var(--fs-ui)",
            padding: 0,
          }}
        >
          {allChecked ? "Limpar" : "Marcar todos"}
        </button>
      </div>
      <ul
        style={{
          listStyle: "none",
          margin: 0,
          padding: 0,
          maxHeight: 180,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {unlabeled.map((o) => (
          <li
            key={o.id}
            style={{
              display: "flex",
              gap: 6,
              alignItems: "center",
              fontSize: "var(--fs-ui)",
              fontFamily: "var(--font-mono)",
            }}
          >
            <input
              type="checkbox"
              checked={checked.has(o.id)}
              onChange={() => toggle(o.id)}
              aria-label={`Selecionar ${o.id}`}
            />
            <button
              type="button"
              onClick={() => onGoToObject(o.id)}
              title="Ir para esta caixa"
              style={{
                flex: 1,
                display: "flex",
                gap: 6,
                alignItems: "baseline",
                background: "transparent",
                border: "none",
                padding: "2px 0",
                cursor: "pointer",
                textAlign: "left",
                fontFamily: "var(--font-mono)",
                fontSize: "var(--fs-ui)",
                color: o.id === selectedObjectId ? colors.primary : colors.text,
              }}
            >
              <span style={{ color: colors.textMuted }}>p{o.page + 1}</span>
              <span>{o.id}</span>
            </button>
          </li>
        ))}
      </ul>
      <label style={{ fontSize: "var(--fs-ui)", color: colors.textMuted }}>
        SKU para os selecionados
      </label>
      <SkuPicker value={batchSku} onChange={setBatchSku} disabled={isMutating} />
      <ActionButton
        label={`Aplicar a ${checked.size} selecionado(s)`}
        shortcut="↵"
        onClick={apply}
        disabled={isMutating || !batchSku || checked.size === 0}
        tone="primary"
      />
    </div>
  );
}

function Swatch({ color }: { color: string }) {
  return (
    <span
      aria-hidden
      style={{
        display: "inline-block",
        width: 14,
        height: 14,
        borderRadius: 2,
        background: color,
        border: `1px solid ${colors.line}`,
      }}
    />
  );
}

// ---------- Shared bits ----------

// Operational workflow state selector (rev03 r3-3). Free transitions — no
// role enforcement yet. "enviado" gets a primary tint as the terminal state.
function OperationalStateSelect({
  value,
  onChange,
  busy,
}: {
  value: OperationalState;
  onChange: (state: OperationalState) => void;
  busy: boolean;
}) {
  const terminal = value === "aprovado" || value === "enviado";
  return (
    <label
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        flex: "0 0 auto",
        fontSize: "var(--fs-ui)",
        color: colors.textMuted,
      }}
    >
      <span>Estado</span>
      <select
        value={value}
        disabled={busy}
        onChange={(e) => onChange(e.target.value as OperationalState)}
        title="Estado operacional do ciclo"
        style={{
          padding: "4px 8px",
          borderRadius: "var(--radius-sm)",
          border: `1px solid ${terminal ? colors.primary : colors.line}`,
          background: terminal ? colors.primary : colors.panel,
          color: terminal ? colors.onPrimary : colors.text,
          fontSize: "var(--fs-ui)",
          fontWeight: 600,
          cursor: busy ? "wait" : "pointer",
        }}
      >
        {OPERATIONAL_STATES.map((s) => (
          <option key={s} value={s}>
            {operationalStateLabel(s)}
          </option>
        ))}
      </select>
    </label>
  );
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1200);
        } catch {
          // Fallback: select-all so the operator can Ctrl+C manually.
        }
      }}
      aria-label={`Copiar ${label}`}
      title={copied ? "Copiado!" : `Copiar ${label}`}
      style={{
        width: 22,
        height: 22,
        padding: 0,
        background: copied ? colors.primary : "transparent",
        color: copied ? colors.onPrimary : colors.textMuted,
        border: `1px solid ${copied ? colors.primary : colors.line}`,
        borderRadius: "var(--radius-sm)",
        cursor: "pointer",
        fontSize: 12,
        lineHeight: 1,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <span aria-hidden>{copied ? "✓" : "⧉"}</span>
    </button>
  );
}


function ReasonList({
  reasons,
  tone,
}: {
  reasons: string[];
  tone: "danger" | "warning";
}) {
  const color = tone === "danger" ? colors.danger : colors.warning;
  return (
    <ul
      style={{
        listStyle: "none",
        margin: 0,
        padding: 0,
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      {reasons.map((reason, idx) => (
        <li
          key={idx}
          style={{
            fontSize: "var(--fs-ui)",
            color,
            display: "flex",
            alignItems: "flex-start",
            gap: 6,
          }}
        >
          <span aria-hidden>•</span>
          <span>{reason}</span>
        </li>
      ))}
    </ul>
  );
}

function SidePanel({
  title,
  children,
  scrollBody = false,
}: {
  title: string;
  children: React.ReactNode;
  // When true the title stays pinned and only the body scrolls — used by the
  // "Tags da página" panel so a long tag list scrolls internally while the
  // stage actions above stay visible (Feedback V2 rev02 r2-8). The minHeight
  // floor (rev03 r3-5) guarantees the panel always renders a usable area even
  // when the stage-actions card above is tall (e.g. Conferência).
  scrollBody?: boolean;
}) {
  return (
    <aside
      style={{
        background: colors.panel,
        border: `1px solid ${colors.line}`,
        borderRadius: "var(--radius-md)",
        padding: "var(--space-base)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-base)",
        fontSize: "var(--fs-body)",
        ...(scrollBody
          ? { flex: "1 1 auto", minHeight: 160, overflow: "hidden" }
          : {}),
      }}
    >
      <h3
        style={{
          fontSize: "var(--fs-title)",
          lineHeight: "var(--lh-title)",
          margin: 0,
          flex: "none",
        }}
      >
        {title}
      </h3>
      {scrollBody ? (
        <div
          style={{
            flex: "1 1 0",
            minHeight: 0,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: "var(--space-base)",
          }}
        >
          {children}
        </div>
      ) : (
        children
      )}
    </aside>
  );
}

function ActionButton({
  label,
  shortcut,
  onClick,
  disabled,
  tone,
  color,
  onColor,
  badge,
}: {
  label: string;
  shortcut: string;
  onClick: () => void;
  disabled: boolean;
  tone: "primary" | "neutral" | "ghost" | "manual";
  // When `color`/`onColor` are set they override the tone palette so a button
  // can carry the exact colour of the thing it acts on — e.g. the Manter A/B
  // buttons painted in the A/B bbox colours (Feedback V2 rev02 r2-3).
  color?: string;
  onColor?: string;
  // Optional letter chip mirroring the on-canvas A/B badge.
  badge?: string;
}) {
  const palette = {
    primary: { bg: colors.primary, fg: colors.onPrimary, border: colors.primary },
    neutral: { bg: colors.neutral, fg: colors.onNeutral, border: colors.neutral },
    manual: { bg: colors.manual, fg: colors.onManual, border: colors.manual },
    ghost: { bg: colors.panel, fg: colors.text, border: colors.line },
  }[tone];
  const bg = color ?? palette.bg;
  const fg = onColor ?? palette.fg;
  const inverted = color ? true : tone !== "ghost";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "8px 12px",
        background: bg,
        color: fg,
        border: `1px solid ${color ?? palette.border}`,
        borderRadius: "var(--radius-sm)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        fontSize: "var(--fs-body)",
        fontWeight: 600,
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
        {badge && (
          <span
            aria-hidden
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 18,
              height: 18,
              borderRadius: 4,
              fontSize: 12,
              fontWeight: 800,
              background: "rgba(255,255,255,0.85)",
              color: bg,
            }}
          >
            {badge}
          </span>
        )}
        {label}
      </span>
      <Kbd inverted={inverted}>{shortcut}</Kbd>
    </button>
  );
}

function Kbd({ children, inverted = false }: { children: React.ReactNode; inverted?: boolean }) {
  return (
    <kbd
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 11,
        padding: "1px 6px",
        borderRadius: 3,
        background: inverted ? "rgba(255,255,255,0.18)" : colors.panelMuted,
        color: inverted ? "inherit" : colors.text,
        border: `1px solid ${inverted ? "rgba(255,255,255,0.3)" : colors.line}`,
      }}
    >
      {children}
    </kbd>
  );
}

// ---------- #16: Conferência continuous scroll ----------

function ContinuousPages({
  cycleId,
  pages,
  allObjects,
  stageWidth,
}: {
  cycleId: string;
  pages: CycleAnnotations["document"]["pages"];
  allObjects: AnnotationObject[];
  stageWidth: number;
}) {
  if (stageWidth <= 0) return null;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-base)",
        width: "100%",
        alignItems: "center",
      }}
    >
      {pages.map((p) => {
        const objs = allObjects.filter(
          (o) =>
            o.page === p.index &&
            (o.status === "confirmed" ||
              o.status === "manual" ||
              o.status === "exception"),
        );
        return (
          <div key={p.index} style={{ width: stageWidth }}>
            <div
              style={{
                fontSize: "var(--fs-ui)",
                color: colors.textMuted,
                fontFamily: "var(--font-mono)",
                marginBottom: 2,
              }}
            >
              página {p.index + 1} · {objs.length} objeto(s)
            </div>
            <LazyPageCanvas
              cycleId={cycleId}
              page={p}
              objects={objs}
              stageWidth={stageWidth}
            />
          </div>
        );
      })}
    </div>
  );
}

// Mounts a read-only PageCanvas only once it scrolls near the viewport, so a
// long cycle doesn't spin up dozens of Konva stages + image loads at once.
function LazyPageCanvas({
  cycleId,
  page,
  objects,
  stageWidth,
}: {
  cycleId: string;
  page: CycleAnnotations["document"]["pages"][number];
  objects: AnnotationObject[];
  stageWidth: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || visible) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setVisible(true);
      },
      { rootMargin: "800px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);
  const aspect = page.heightPt / Math.max(page.widthPt, 1);
  const height = Math.round(stageWidth * aspect);
  return (
    <div ref={ref} style={{ minHeight: height }}>
      {visible ? (
        <PageCanvas
          cycleId={cycleId}
          page={page}
          objects={objects}
          stageWidth={stageWidth}
          selectedObjectId={null}
          onSelect={() => {}}
        />
      ) : (
        <div
          style={{
            height,
            background: colors.canvas,
            border: `1px dashed ${colors.line}`,
            borderRadius: 4,
          }}
        />
      )}
    </div>
  );
}

const railToggleStyle: React.CSSProperties = {
  width: 24,
  height: 24,
  padding: 0,
  background: "transparent",
  color: colors.textMuted,
  border: `1px solid ${colors.line}`,
  borderRadius: "var(--radius-sm)",
  cursor: "pointer",
  fontSize: 13,
  lineHeight: 1,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

function pageNavBtn(disabled: boolean): React.CSSProperties {
  return {
    width: 26,
    height: 26,
    padding: 0,
    background: colors.panel,
    color: disabled ? colors.line : colors.text,
    border: `1px solid ${colors.line}`,
    borderRadius: "var(--radius-sm)",
    cursor: disabled ? "not-allowed" : "pointer",
    fontSize: 16,
    lineHeight: 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  };
}

function ThumbnailRail({
  cycleId,
  pages,
  activeIndex,
  onSelect,
  perPageCounts,
  collapsed,
  onToggleCollapse,
}: {
  cycleId: string;
  pages: CycleAnnotations["document"]["pages"];
  activeIndex: number;
  onSelect: (idx: number) => void;
  perPageCounts: PerPageCount[];
  collapsed: boolean;
  onToggleCollapse: () => void;
}) {
  if (collapsed) {
    return (
      <nav
        aria-label="Páginas (recolhido)"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 6,
          background: colors.panel,
          border: `1px solid ${colors.line}`,
          borderRadius: "var(--radius-md)",
          padding: "var(--space-tight)",
          position: "sticky",
          top: "var(--space-base)",
        }}
      >
        <button
          type="button"
          onClick={onToggleCollapse}
          title="Expandir miniaturas das páginas"
          aria-label="Expandir miniaturas das páginas"
          style={railToggleStyle}
        >
          ▸
        </button>
        <span
          className="mono"
          style={{
            fontSize: "var(--fs-ui)",
            color: colors.textMuted,
            writingMode: "vertical-rl",
          }}
        >
          {activeIndex + 1}/{pages.length}
        </span>
      </nav>
    );
  }
  return (
    <nav
      aria-label="Páginas"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        background: colors.panel,
        border: `1px solid ${colors.line}`,
        borderRadius: "var(--radius-md)",
        padding: "var(--space-tight)",
        maxHeight: "calc(100vh - 220px)",
        overflowY: "auto",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 6,
          padding: "0 2px 2px",
        }}
      >
        <span
          style={{
            fontSize: "var(--fs-ui)",
            color: colors.textMuted,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          Páginas
        </span>
        <button
          type="button"
          onClick={onToggleCollapse}
          title="Recolher miniaturas das páginas"
          aria-label="Recolher miniaturas das páginas"
          style={railToggleStyle}
        >
          ◂
        </button>
      </div>
      {pages.map((p, idx) => {
        const isActive = idx === activeIndex;
        const counts = perPageCounts[idx];
        return (
          <button
            key={p.index}
            type="button"
            onClick={() => onSelect(idx)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: 6,
              background: isActive ? colors.primary : "transparent",
              color: isActive ? colors.onPrimary : colors.text,
              border: "none",
              borderRadius: "var(--radius-sm)",
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            {preferredAssetUrl(cycleId, p.thumbnailKey, p.thumbnailUrl) ? (
              <img
                src={preferredAssetUrl(cycleId, p.thumbnailKey, p.thumbnailUrl)!}
                alt={`thumb p${p.index + 1}`}
                style={{
                  width: 56,
                  height: "auto",
                  borderRadius: 2,
                  border: `1px solid ${isActive ? colors.onPrimary : colors.line}`,
                  background: colors.canvas,
                }}
              />
            ) : (
              <div
                style={{
                  width: 56,
                  height: 72,
                  background: colors.canvas,
                  border: `1px solid ${colors.line}`,
                }}
              />
            )}
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "var(--fs-ui)",
                lineHeight: 1.3,
              }}
            >
              p{p.index + 1}
              <br />
              <span style={{ opacity: 0.85 }}>
                {counts ? `${counts.useful}✓ ${counts.trash}× ${counts.pending}?` : "0 obj"}
              </span>
              {counts && counts.pendingPairs > 0 && (
                <>
                  <br />
                  <span style={{ color: isActive ? colors.onPrimary : colors.danger }}>
                    {counts.pendingPairs} sobrep.
                  </span>
                </>
              )}
              {counts && counts.pendingConflicts > 0 && (
                <>
                  <br />
                  <span style={{ color: isActive ? colors.onPrimary : colors.warning }}>
                    {counts.pendingConflicts} 3B
                  </span>
                </>
              )}
            </span>
          </button>
        );
      })}
    </nav>
  );
}

function SummaryRow({
  data,
  totals,
  pendingPairs,
  pendingTrash,
  isLocked1,
  isLocked2,
  onJump,
}: {
  data: CycleAnnotations;
  totals: { useful: number; trash: number; pending: number; total: number };
  pendingPairs: number;
  pendingTrash: number;
  isLocked1: boolean;
  isLocked2: boolean;
  onJump: (target: "suggested" | "trash" | "overlap") => void;
}) {
  return (
    <div style={{ display: "flex", gap: "var(--space-base)", flexWrap: "wrap" }}>
      <Stat label="Status" value={cycleStatusLabel(data.status)} accent />
      {isLocked1 && (
        <Stat label="Conferência" value="🔒" mono color={colors.primary} />
      )}
      {isLocked2 && (
        <Stat label="Classificação" value="🔒" mono color={colors.primary} />
      )}
      <Stat label="Páginas" value={String(data.document.pageCount)} mono />
      <Stat label="Objetos" value={String(totals.total)} mono />
      <Stat label="Útil" value={String(totals.useful)} mono color={colors.primary} />
      {/* These three are actionable: clicking jumps to that work (#8). */}
      <Stat
        label="Trash"
        value={String(pendingTrash)}
        mono
        color={colors.neutral}
        onClick={pendingTrash > 0 ? () => onJump("trash") : undefined}
        hint="Ir para Conferir Trash"
      />
      <Stat
        label="Pendente"
        value={String(totals.pending)}
        mono
        color={colors.intelligence}
        onClick={totals.pending > 0 ? () => onJump("suggested") : undefined}
        hint="Ir para a próxima pendência (Triagem)"
      />
      <Stat
        label="Sobrep."
        value={String(pendingPairs)}
        mono
        color={colors.danger}
        onClick={pendingPairs > 0 ? () => onJump("overlap") : undefined}
        hint="Ir para Sobreposições"
      />
    </div>
  );
}

function Stat({
  label,
  value,
  accent = false,
  mono = false,
  color,
  onClick,
  hint,
}: {
  label: string;
  value: string;
  accent?: boolean;
  mono?: boolean;
  color?: string;
  onClick?: () => void;
  hint?: string;
}) {
  const valueColor = color ?? (accent ? colors.intelligence : colors.text);
  const clickable = Boolean(onClick);
  const baseStyle: React.CSSProperties = {
    background: colors.panel,
    border: `1px solid ${clickable ? valueColor : colors.line}`,
    borderRadius: "var(--radius-md)",
    padding: "10px 14px",
    minWidth: 100,
    textAlign: "left",
    cursor: clickable ? "pointer" : "default",
  };
  const inner = (
    <>
      <div
        style={{
          fontSize: "var(--fs-ui)",
          color: colors.textMuted,
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 600,
          color: valueColor,
          fontFamily: mono ? "var(--font-mono)" : "var(--font-body)",
        }}
      >
        {value}
      </div>
    </>
  );
  if (clickable) {
    return (
      <button type="button" onClick={onClick} title={hint} style={baseStyle}>
        {inner}
      </button>
    );
  }
  return <div style={baseStyle}>{inner}</div>;
}

function ProcessingPlaceholder({ status }: { status: string }) {
  return (
    <div
      style={{
        background: colors.canvas,
        border: `1px dashed ${colors.line}`,
        borderRadius: "var(--radius-md)",
        padding: "var(--space-roomy)",
        textAlign: "center",
        color: colors.textMuted,
      }}
    >
      <div style={{ fontSize: "var(--fs-title)", color: colors.text, marginBottom: 8 }}>
        Processando…
      </div>
      <div style={{ fontSize: "var(--fs-ui)" }}>
        status: {cycleStatusLabel(status)}
      </div>
      <div style={{ marginTop: 12, fontSize: "var(--fs-ui)" }}>
        Esta página atualiza automaticamente quando a extração concluir.
      </div>
    </div>
  );
}

function FailedNotice() {
  return (
    <div
      style={{
        background: colors.panel,
        border: `1px solid ${colors.danger}`,
        borderRadius: "var(--radius-md)",
        padding: "var(--space-base)",
        color: colors.danger,
      }}
    >
      O processamento falhou. Verifique os logs do worker para detalhes.
    </div>
  );
}

function countByStatus(objects: AnnotationObject[]): {
  useful: number;
  trash: number;
  pending: number;
  total: number;
} {
  let useful = 0;
  let trash = 0;
  let pending = 0;
  for (const o of objects) {
    if (o.status === "confirmed" || o.status === "manual" || o.status === "exception") {
      // Kept outputs. rev03 r3-2: manual marks are operator additions, not
      // pending work — they must NOT inflate the Pendente counter (which the
      // operator reads as "still open" and could block completion).
      useful++;
    } else if (o.status === "trash") {
      trash++;
    } else {
      // Only `suggested` is genuinely pending (awaiting a triage verdict).
      pending++;
    }
  }
  return { useful, trash, pending, total: objects.length };
}

function countObjectsByPage(
  objects: AnnotationObject[],
  pairs: OverlapPairWire[],
  conflicts: ManualTrashConflictWire[],
  pages: CycleAnnotations["document"]["pages"],
): PerPageCount[] {
  const empty: PerPageCount = {
    useful: 0,
    trash: 0,
    pending: 0,
    pendingPairs: 0,
    pendingConflicts: 0,
  };
  const counts = new Map<number, PerPageCount>();
  const get = (page: number): PerPageCount => counts.get(page) ?? { ...empty };
  for (const o of objects) {
    const c = get(o.page);
    // Mirror countByStatus: manual/exception are kept (useful), only
    // suggested is pending (rev03 r3-2).
    if (o.status === "confirmed" || o.status === "manual" || o.status === "exception") {
      c.useful++;
    } else if (o.status === "trash") {
      c.trash++;
    } else {
      c.pending++;
    }
    counts.set(o.page, c);
  }
  for (const p of pairs) {
    if (p.decision !== "pending") continue;
    const c = get(p.page);
    c.pendingPairs++;
    counts.set(p.page, c);
  }
  for (const cf of conflicts) {
    if (cf.decision !== "pending") continue;
    const c = get(cf.page);
    c.pendingConflicts++;
    counts.set(cf.page, c);
  }
  return pages.map((p) => counts.get(p.index) ?? { ...empty });
}
